import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfMonth,
  startOfMonth,
  subMonths,
  format,
  isSameDay,
} from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input, SegmentedTabs } from "@akuna/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Download,
  Activity,
  CheckCircle2,
  Receipt,
  Stethoscope,
  Users,
} from "lucide-react";
import { useReportsOverview } from "@/hooks/useReportsOverview";
import { useReportsByProfessional } from "@/hooks/useReportsByProfessional";
import { useReportsHeatmap } from "@/hooks/useReportsHeatmap";
import { getComparisonRange, type CompareMode } from "@/lib/utils/comparison-dates";
import { AppointmentsPeriodHero } from "@/components/reports/appointments/AppointmentsPeriodHero";
import { AppointmentsDailyVolumeChart } from "@/components/reports/appointments/AppointmentsDailyVolumeChart";
import { AppointmentsHeatmap } from "@/components/reports/appointments/AppointmentsHeatmap";
import { AppointmentsFunnel } from "@/components/reports/appointments/AppointmentsFunnel";
import { AppointmentTypesPanel } from "@/components/reports/appointments/AppointmentTypesPanel";
import { useReportsFinancial, type AbcCurve, type TopOwnerRow } from "@/hooks/useReportsFinancial";
import { useRevenueReport } from "@/hooks/useRevenueReport";
import { RevenuePeriodHero } from "@/components/reports/revenue/RevenuePeriodHero";
import { RevenueKPIs } from "@/components/reports/revenue/RevenueKPIs";
import { RevenueLineChart } from "@/components/reports/revenue/RevenueLineChart";
import { RevenuePaymentMethods } from "@/components/reports/revenue/RevenuePaymentMethods";
import { RevenueCategoryMargin } from "@/components/reports/revenue/RevenueCategoryMargin";
import { RevenueReceivables } from "@/components/reports/revenue/RevenueReceivables";
import { RevenueAbcCurve } from "@/components/reports/revenue/RevenueAbcCurve";
import { useReportsComparatives } from "@/hooks/useReportsComparatives";
import { useReportsInactiveOwners } from "@/hooks/useReportsInactiveOwners";
import { useReportsOverdueVaccines, useReportsUpcomingApplications } from "@/hooks/useReportsOverdueVaccines";
import { useReportsStock } from "@/hooks/useReportsStock";
import {
  useBaseExplorer,
  type ExplorerRow,
  type ExplorerAbcCurve,
  type AgeGroup,
  type DetailedAgeGroup,
  type RelationshipStatus,
} from "@/hooks/useBaseExplorer";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import { Progress } from "@/components/ui/progress";
import { getItemTypeBadgeClass } from "@/lib/item-type-badge";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";
import ApplicationReport from "@/pages/applications/ApplicationReport";
import { CampaignsPage } from "@/pages/reports/Campaigns";
import { useCampaigns } from "@/hooks/useCampaigns";
import { exportToExcel, exportToExcelMultiSheet } from "@/lib/utils/export";
import { useTranslation } from "react-i18next";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";
import { ProfessionalsPeriodHero } from "@/components/reports/professionals/ProfessionalsPeriodHero";
import { StockPeriodHero } from "@/components/reports/stock/StockPeriodHero";
import { SalesExplorerTab } from "@/pages/reports/sales-explorer/SalesExplorerTab";
import { abcCurve } from "@/lib/reports/abc";
import { PrescriptionsReportTab } from "@/pages/reports/prescriptions/PrescriptionsReportTab";
import PlansTab from "@/pages/daycare-hotel/tabs/PlansTab";
import { useModuleAccess } from "@/modules/useModuleAccess";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { getStayReportPath, getStayReportTabKey } from "@/pages/reports/daycare-hotel-route";
import { DeltaBadge } from "@/components/reports/DeltaBadge";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PERIOD_OPTIONS = [
  { value: "day", labelKey: "periodToday" },
  { value: "week", labelKey: "periodThisWeek" },
  { value: "month", labelKey: "periodThisMonth" },
  { value: "3months", labelKey: "periodLast3Months" },
  { value: "6months", labelKey: "periodLast6Months" },
  { value: "12months", labelKey: "periodLast12Months" },
] as const;

type PeriodValue = (typeof PERIOD_OPTIONS)[number]["value"];

const INACTIVE_DAYS_OPTIONS = [60, 90, 180] as const;

type RevenueBusinessCategory =
  | "consultation"
  | "application"
  | "exam"
  | "surgery"
  | "hospitalization"
  | "grooming"
  | "package"
  | "product"
  | "service"
  | "other";

type RevenueCategoryItem = {
  itemName: string;
  type: "PRODUTO" | "SERVICO" | "PACOTE";
};

const normalizeSearchText = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getAppointmentTypeDataKey = (type: string) =>
  `type_${normalizeSearchText(type).replace(/[^a-z0-9]+/g, "_")}`;

function getRevenueBusinessCategory(item: RevenueCategoryItem): RevenueBusinessCategory {
  const name = normalizeSearchText(item.itemName);

  if (item.type === "PRODUTO") return "product";
  if (item.type === "PACOTE") return "package";
  if (/(vacina|vacinacao|aplicacao|dose|imuniz|vermifug|antirrabic)/.test(name)) return "application";
  if (/(exame|hemograma|ultrassom|ultra|raio|radiograf|laborator|teste|bioquim)/.test(name)) return "exam";
  if (/(cirurgia|cirurg|anestesia|sutura|castracao|procedimento cirurg)/.test(name)) return "surgery";
  if (/(internacao|internamento|diaria|uti|hospital)/.test(name)) return "hospitalization";
  if (/(banho|tosa|grooming|higien|desembol)/.test(name)) return "grooming";
  if (/(consulta|consult|retorno|checkup|check-up|avaliacao)/.test(name)) return "consultation";
  if (item.type === "SERVICO") return "service";

  return "other";
}

function getDateRange(period: PeriodValue): { dateFrom: Date; dateTo: Date } {
  const now = new Date();
  switch (period) {
    case "day":
      return { dateFrom: startOfDay(now), dateTo: endOfDay(now) };
    case "week":
      return {
        dateFrom: startOfWeek(now, { weekStartsOn: 0 }),
        dateTo: endOfDay(now),
      };
    case "month":
      return { dateFrom: startOfMonth(now), dateTo: endOfDay(now) };
    case "3months":
      return { dateFrom: startOfMonth(subMonths(now, 2)), dateTo: endOfDay(now) };
    case "6months":
      return { dateFrom: startOfMonth(subMonths(now, 5)), dateTo: endOfDay(now) };
    case "12months":
      return { dateFrom: startOfMonth(subMonths(now, 11)), dateTo: endOfDay(now) };
    default:
      return { dateFrom: startOfMonth(now), dateTo: endOfDay(now) };
  }
}

const PAGE_SIZE = 10;

type ProductsTypeFilter = "all" | "PRODUTO" | "SERVICO" | "PACOTE";

const SORT_PRODUCTS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "revenue_desc", labelKey: "revenueDesc" },
  { value: "revenue_asc", labelKey: "revenueAsc" },
  { value: "itemName_asc", labelKey: "nameAZ" },
  { value: "itemName_desc", labelKey: "nameZA" },
  { value: "type_asc", labelKey: "typeAZ" },
  { value: "type_desc", labelKey: "typeZA" },
  { value: "quantity_desc", labelKey: "qtyDesc" },
  { value: "quantity_asc", labelKey: "qtyAsc" },
];

const SORT_OWNERS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "totalSpent_desc", labelKey: "totalSpentDesc" },
  { value: "totalSpent_asc", labelKey: "totalSpentAsc" },
  { value: "saleCount_desc", labelKey: "salesDesc" },
  { value: "saleCount_asc", labelKey: "salesAsc" },
  { value: "ownerName_asc", labelKey: "nameAZ" },
  { value: "ownerName_desc", labelKey: "nameZA" },
];

const SORT_OUTFLOW_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "revenue_desc", labelKey: "revenueDesc" },
  { value: "revenue_asc", labelKey: "revenueAsc" },
  { value: "quantity_desc", labelKey: "quantityDesc" },
  { value: "quantity_asc", labelKey: "quantityAsc" },
  { value: "itemName_asc", labelKey: "nameAZ" },
  { value: "itemName_desc", labelKey: "nameZA" },
];

const APPOINTMENT_TYPE_COLOR_CLASSES: Record<string, string> = {
  consulta: "bg-blue-500",
  cirurgia: "bg-red-500",
  banho_tosa: "bg-green-500",
  retorno: "bg-yellow-500",
  aplicacao: "bg-indigo-500",
  vacina: "bg-indigo-500",
  outro: "bg-muted-foreground",
};

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function AbcBadge({ curve }: { curve: AbcCurve }) {
  const cls =
    curve === "A"
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : curve === "B"
        ? "bg-yellow-100 text-yellow-800 border-yellow-200"
        : "bg-red-100 text-red-800 border-red-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${cls}`}>
      {curve}
    </span>
  );
}

// Compute ABC curve + cumulative % in-place (returns enriched copy)
function computeAbcCurve(owners: TopOwnerRow[]): TopOwnerRow[] {
  const sorted = [...owners].sort((a, b) => b.totalSpent - a.totalSpent);
  const total = sorted.reduce((s, o) => s + o.totalSpent, 0);
  if (total === 0) return sorted.map((o) => ({ ...o, abcCurve: "C" as AbcCurve, cumulativePct: 1 }));
  const curve = abcCurve(sorted.map((o) => o.totalSpent));
  return sorted.map((o, i) => ({ ...o, abcCurve: curve[i].abc as AbcCurve, cumulativePct: curve[i].cumulativeShare }));
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Reports() {
  const { t, i18n } = useTranslation("reports");
  useMobileHeader({ title: t("title") }, [t]);
  const navigate = useNavigate();
  const { reportTab, applicationTab } = useParams<{
    reportTab?: string;
    applicationTab?: string;
  }>();
  const isPt = i18n.resolvedLanguage?.startsWith("pt") ?? i18n.language?.startsWith("pt") ?? true;
  const dateLocale = isPt ? ptBR : enUS;
  const numberLocale = isPt ? "pt-BR" : "en-US";

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(numberLocale, { style: "currency", currency: "BRL" }).format(value);

  const formatDate = (d: Date) => format(d, "dd/MM/yyyy", { locale: dateLocale });

  const getAppointmentTypeLabel = useCallback((type: string) => {
    const labels: Record<string, string> = {
      consulta: t("appointmentTypeConsulta"),
      cirurgia: t("appointmentTypeCirurgia"),
      banho_tosa: t("appointmentTypeBanhoTosa"),
      retorno: t("appointmentTypeRetorno"),
      aplicacao: t("appointmentTypeAplicacao"),
      vacina: t("appointmentTypeAplicacao"),
      outro: t("appointmentTypeOutro"),
    };
    return labels[type] ?? type;
  }, [t]);

  const { data: permissionsMap, isLoading: isLoadingPermissions } = usePermissionsMap();
  const daycareHotelAccess = useDaycareHotelAccess();
  const { has: hasModule, isLoading: isLoadingModules } = useModuleAccess();
  const canViewReports = permissionsMap?.reports?.view === true;
  const canViewApplications = permissionsMap?.applications?.view === true;
  const canViewPlans = canViewReports && !daycareHotelAccess.isLoading && daycareHotelAccess.mode !== "none";
  // Revenue/professionals/sales-explorer belong to the financial module; the stock
  // tab belongs to the stock module. The "professional" (Atendimentos) and "explorer"
  // tabs are core, so they stay tied only to canViewReports.
  const canViewFinancialReports = canViewReports && hasModule("financial");
  const canViewStockReports = canViewReports && hasModule("stock");

  type ReportsTabValue =
    | "professional"
    | "financial"
    | "professionals"
    | "stock"
    | "salesExplorer"
    | "explorer"
    | "campaigns"
    | "applications"
    | "plans"
    | "prescriptions";

  const availableTabs = useMemo<ReportsTabValue[]>(() => {
    const tabs: ReportsTabValue[] = [];
    if (canViewReports) {
      tabs.push("professional");
      if (canViewFinancialReports) tabs.push("financial", "professionals", "salesExplorer");
      if (canViewStockReports) tabs.push("stock");
      if (canViewPlans) tabs.push("plans");
      tabs.push("prescriptions");
    }
    if (canViewApplications) tabs.push("applications");
    return tabs;
  }, [canViewApplications, canViewFinancialReports, canViewPlans, canViewReports, canViewStockReports]);

  const defaultTab = useMemo<ReportsTabValue>(() => {
    if (canViewReports) return "professional";
    return "applications";
  }, [canViewReports]);

  const routeTab = useMemo<ReportsTabValue | null>(() => {
    if (applicationTab) return "applications";
    if (!reportTab) return null;
    const aliases: Record<string, ReportsTabValue> = {
      appointments: "professional",
      painel: "professional",
      receita: "financial",
      atendimentos: "professional",
      base: "explorer",
      billing: "financial",
      dormant: "explorer",
      inactive: "explorer",
      "sales-explorer": "salesExplorer",
      "daycare-hotel": "plans",
      receitas: "prescriptions",
      plans: "plans",
    };
    if (aliases[reportTab]) return aliases[reportTab];
    const valid: ReportsTabValue[] = [
      "professional",
      "financial",
      "professionals",
      "stock",
      "salesExplorer",
      "explorer",
      "campaigns",
      "applications",
      "plans",
      "prescriptions",
    ];
    return valid.includes(reportTab as ReportsTabValue) ? (reportTab as ReportsTabValue) : null;
  }, [applicationTab, reportTab]);

  const [period, setPeriod] = useState<PeriodValue>("month");

  // Atendimentos tab — custom date range + comparison
  const [proDateFrom, setProDateFrom] = useState<Date>(() => startOfMonth(new Date()));
  const [proDateTo, setProDateTo] = useState<Date>(() => endOfDay(new Date()));
  const [compareMode, setCompareMode] = useState<CompareMode>("prev-month");

  // Professional deep-dive
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<string | null>(null);

  // Professionals tab — own date range + comparison
  const [profDateFrom, setProfDateFrom] = useState<Date>(() => startOfMonth(new Date()));
  const [profDateTo, setProfDateTo] = useState<Date>(() => endOfDay(new Date()));
  // Patient pagination per professional (keyed by rowId)
  const [patientPages, setPatientPages] = useState<Record<string, number>>({});
  // Commission-by-product pagination per professional (keyed by rowId)
  const [commissionPages, setCommissionPages] = useState<Record<string, number>>({});

  // Revenue tab date state
  const [finDateFrom, setFinDateFrom] = useState<Date>(() => startOfMonth(new Date()));
  const [finDateTo, setFinDateTo] = useState<Date>(() => endOfDay(new Date()));
  const [finCompareMode, setFinCompareMode] = useState<CompareMode>("prev-month");

  // Financial filters
  const [pageProducts, setPageProducts] = useState(1);
  const [pageOwners, setPageOwners] = useState(1);
  const [filterProductsType, setFilterProductsType] = useState<ProductsTypeFilter>("all");
  const [filterProductsSearch, setFilterProductsSearch] = useState("");
  const [sortProducts, setSortProducts] = useState("revenue_desc");
  const [filterOwnersSearch, setFilterOwnersSearch] = useState("");
  const [filterOwnersAbc, setFilterOwnersAbc] = useState<AbcCurve | "all">("all");
  const [filterOwnersSpecies, setFilterOwnersSpecies] = useState<string>("all");
  const [filterOwnersBreed, setFilterOwnersBreed] = useState<string>("all");
  const [sortOwners, setSortOwners] = useState("totalSpent_desc");

  // Stock tab — own date range
  const [stockDateFrom, setStockDateFrom] = useState<Date>(() => startOfMonth(new Date()));
  const [stockDateTo, setStockDateTo] = useState<Date>(() => endOfDay(new Date()));

  // Stock tab filters
  const [filterOutflowSearch, setFilterOutflowSearch] = useState("");
  const [sortOutflow, setSortOutflow] = useState("revenue_desc");
  const [pageOutflow, setPageOutflow] = useState(1);

  // Dormant owners filters
  const [inactiveDays, setInactiveDays] = useState<number>(90);
  const [inactiveSearch, setInactiveSearch] = useState("");
  const [pageInactive, setPageInactive] = useState(1);

  // Explorer de Base filters
  const [explorerSearch, setExplorerSearch] = useState("");
  const [explorerSpecies, setExplorerSpecies] = useState<string>("all");
  const [explorerBreed, setExplorerBreed] = useState<string>("all");
  const [explorerGender, setExplorerGender] = useState<string>("all");
  const [explorerSize, setExplorerSize] = useState<string>("all");
  const [explorerCoatType, setExplorerCoatType] = useState<string>("all");
  const [explorerPetStatus, setExplorerPetStatus] = useState<"all" | "alive" | "deceased">("all");
  const [explorerLastVisitDays, setExplorerLastVisitDays] = useState<string>("all");
  const [explorerAbc, setExplorerAbc] = useState<ExplorerAbcCurve | "all">("all");
  const [explorerHasPhone, setExplorerHasPhone] = useState<"all" | "yes" | "no">("all");
  const [explorerHasWhatsApp, setExplorerHasWhatsApp] = useState<"all" | "yes" | "no">("all");
  const [explorerAgeGroup, setExplorerAgeGroup] = useState<AgeGroup | "all">("all");
  const [explorerDetailedAgeGroup, setExplorerDetailedAgeGroup] = useState<DetailedAgeGroup | "all">("all");
  const [explorerAgeMin, setExplorerAgeMin] = useState("");
  const [explorerAgeMax, setExplorerAgeMax] = useState("");
  const [explorerSterilized, setExplorerSterilized] = useState<"all" | "yes" | "no">("all");
  const [explorerRelationshipStatus, setExplorerRelationshipStatus] = useState<RelationshipStatus | "all">("all");
  const [explorerMinVisits, setExplorerMinVisits] = useState("");
  const [explorerMinTicket, setExplorerMinTicket] = useState("");
  const [explorerWeightStatus, setExplorerWeightStatus] = useState<"all" | "with" | "missing" | "stale">("all");
  const [pageExplorer, setPageExplorer] = useState(1);

  const activeTab = useMemo<ReportsTabValue>(() => {
    if (routeTab && availableTabs.includes(routeTab)) return routeTab;
    return availableTabs[0] ?? defaultTab;
  }, [availableTabs, defaultTab, routeTab]);

  const reportPathForTab = useCallback((tab: ReportsTabValue) => {
    const paths: Record<ReportsTabValue, string> = {
      financial: "/reports/receita",
      professional: "/reports/appointments",
      professionals: "/reports/professionals",
      explorer: "/reports/base",
      salesExplorer: "/reports/sales-explorer",
      stock: "/reports/stock",
      campaigns: "/reports/campaigns",
      applications: "/reports/applications/pending",
      plans: getStayReportPath(),
      prescriptions: "/reports/prescriptions",
    };
    return paths[tab];
  }, []);

  // Reset professional deep-dive when leaving the tab
  useEffect(() => {
    if (activeTab !== "professional") setSelectedProfessionalId(null);
  }, [activeTab]);

  useEffect(() => {
    if (routeTab && availableTabs.includes(routeTab)) return;
    // as abas de Receita/Profissionais/Explorador dependem dos módulos: sem esperar por eles,
    // um link direto (/reports/receita) caía em Atendimentos
    if (isLoadingPermissions || permissionsMap === undefined || isLoadingModules || daycareHotelAccess.isLoading) return;
    const fallbackTab = availableTabs[0] ?? defaultTab;
    navigate(reportPathForTab(fallbackTab), { replace: true });
  }, [availableTabs, daycareHotelAccess.isLoading, defaultTab, isLoadingModules, isLoadingPermissions, navigate, permissionsMap, reportPathForTab, routeTab]);

  useEffect(() => {
    if (reportTab === "atendimentos") {
      navigate(reportPathForTab("professional"), { replace: true });
    }
  }, [navigate, reportPathForTab, reportTab]);

  useEffect(() => {
    if (reportTab === "plans") {
      navigate(reportPathForTab("plans"), { replace: true });
    }
  }, [navigate, reportPathForTab, reportTab]);

  const showPeriodSelect =
    canViewReports &&
    activeTab === "financial";

  const isProfessionalTabActive = canViewReports && activeTab === "professional";
  const isProfessionalsTabActive = canViewFinancialReports && activeTab === "professionals";
  const isFinancialTabActive = canViewFinancialReports && activeTab === "financial";
  const isStockTabActive = canViewStockReports && activeTab === "stock";
  const isExplorerTabActive = canViewReports && activeTab === "explorer";
  const isApplicationsTabActive = canViewApplications && activeTab === "applications";

  const { dateFrom, dateTo } = useMemo(() => {
    if (!canViewReports) return { dateFrom: null, dateTo: null };
    return getDateRange(period);
  }, [canViewReports, period]);

  // Financial tab overview uses period-based dates; professional tab uses its own range
  const overviewDateFrom = isFinancialTabActive ? dateFrom : isProfessionalTabActive ? proDateFrom : null;
  const overviewDateTo = isFinancialTabActive ? dateTo : isProfessionalTabActive ? proDateTo : null;
  const professionalDateFrom = isProfessionalTabActive ? proDateFrom : isFinancialTabActive ? dateFrom : null;
  const professionalDateTo = isProfessionalTabActive ? proDateTo : isFinancialTabActive ? dateTo : null;

  // Comparison range for Atendimentos tab
  const comparisonRange = isProfessionalTabActive
    ? getComparisonRange(proDateFrom, proDateTo, compareMode)
    : null;
  // Revenue uses its own financial query; stock uses its own date state.
  const financialDateFrom = isFinancialTabActive ? dateFrom : null;
  const financialDateTo = isFinancialTabActive ? dateTo : null;
  const previousFinancialRange = useMemo(() => {
    if (!isFinancialTabActive || !dateFrom || !dateTo) return { dateFrom: null, dateTo: null };
    const periodMs = dateTo.getTime() - dateFrom.getTime();
    const previousDateTo = new Date(dateFrom.getTime() - 1);
    const previousDateFrom = new Date(previousDateTo.getTime() - periodMs);
    return { dateFrom: previousDateFrom, dateTo: previousDateTo };
  }, [isFinancialTabActive, dateFrom, dateTo]);

  const { data: overview, isLoading: loadingOverview } = useReportsOverview(
    overviewDateFrom,
    overviewDateTo
  );
  const { data: comparisonOverview } = useReportsOverview(
    isProfessionalTabActive ? comparisonRange?.compareFrom ?? null : null,
    isProfessionalTabActive ? comparisonRange?.compareTo ?? null : null,
  );
  const { data: heatmapData = [], isLoading: loadingHeatmap } = useReportsHeatmap(
    isProfessionalTabActive ? proDateFrom : null,
    isProfessionalTabActive ? proDateTo : null,
  );
  const { data: comparative } = useReportsComparatives(
    overviewDateFrom,
    overviewDateTo,
    "month_vs_month"
  );
  const { data: byProfessional = [], isLoading: loadingByProfessional } =
    useReportsByProfessional(professionalDateFrom, professionalDateTo);
  const { data: byProfessionalTab = [], isLoading: loadingByProfessionalTab } =
    useReportsByProfessional(
      isProfessionalsTabActive ? profDateFrom : null,
      isProfessionalsTabActive ? profDateTo : null,
    );
  const { data: financial, isLoading: loadingFinancial } = useReportsFinancial(
    financialDateFrom,
    financialDateTo
  );
  const { data: stockFinancial, isLoading: loadingStockFinancial } = useReportsFinancial(
    isStockTabActive ? stockDateFrom : null,
    isStockTabActive ? stockDateTo : null,
  );

  // Revenue tab — own date state + comparison
  const finCompareRange = finCompareMode !== "none"
    ? getComparisonRange(finDateFrom, finDateTo, finCompareMode)
    : null;
  const { data: revenueReport, isLoading: loadingRevenue } = useRevenueReport(
    isFinancialTabActive ? finDateFrom : null,
    isFinancialTabActive ? finDateTo : null,
    isFinancialTabActive ? (finCompareRange?.compareFrom ?? null) : null,
    isFinancialTabActive ? (finCompareRange?.compareTo ?? null) : null
  );
  const { data: previousFinancial } = useReportsFinancial(
    previousFinancialRange.dateFrom,
    previousFinancialRange.dateTo
  );
  const { data: stock, isLoading: loadingStock } = useReportsStock(
    isStockTabActive ? stockDateFrom : null,
    isStockTabActive ? stockDateTo : null,
  );
  const { data: inactiveOwners = [], isLoading: loadingInactive } =
    useReportsInactiveOwners(isExplorerTabActive ? inactiveDays : 90);
  const { data: overdueVaccines = [], isLoading: loadingOverdueVaccines } = useReportsOverdueVaccines();
  const { data: upcomingApplications = [], isLoading: loadingUpcomingApplications } = useReportsUpcomingApplications(30);
  const { data: explorerRows = [], isLoading: loadingExplorer } = useBaseExplorer();
  const { data: campaigns = [] } = useCampaigns();

  // ------------------------------------------------------------------
  // ABC curve computation (memoized)
  // ------------------------------------------------------------------
  const ownersWithAbc = useMemo(
    () => computeAbcCurve(financial?.topOwners ?? []),
    [financial?.topOwners]
  );

  const abcStrategicSummary = useMemo(() => {
    const totalRevenue = ownersWithAbc.reduce((sum, owner) => sum + owner.totalSpent, 0);
    const clientsA = ownersWithAbc.filter((owner) => owner.abcCurve === "A");
    const clientsB = ownersWithAbc.filter((owner) => owner.abcCurve === "B");
    const clientsC = ownersWithAbc.filter((owner) => owner.abcCurve === "C");
    const clientsARevenue = clientsA.reduce((sum, owner) => sum + owner.totalSpent, 0);
    const topClient = ownersWithAbc[0] ?? null;

    return {
      totalRevenue,
      clientsA,
      clientsB,
      clientsC,
      clientsARevenue,
      clientsAShare: totalRevenue > 0 ? (clientsARevenue / totalRevenue) * 100 : 0,
      topClient,
    };
  }, [ownersWithAbc]);

  // Unique species/breeds for filter dropdowns
  const allSpecies = useMemo(() => {
    const map: Record<string, string> = {};
    ownersWithAbc.forEach((o) => {
      o.speciesIds.forEach((id, i) => {
        map[id] = o.speciesNames[i] ?? id;
      });
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [ownersWithAbc]);

  const allBreeds = useMemo(() => {
    const map: Record<string, string> = {};
    ownersWithAbc
      .filter((o) => filterOwnersSpecies === "all" || o.speciesIds.includes(filterOwnersSpecies))
      .forEach((o) => {
        o.breedIds.forEach((id, i) => {
          map[id] = o.breedNames[i] ?? id;
        });
      });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [ownersWithAbc, filterOwnersSpecies]);

  // ------------------------------------------------------------------
  // Billing by business category (Phase 1.5.2)
  // ------------------------------------------------------------------
  const billingByCategory = useMemo(() => {
    const items = financial?.topItems ?? [];
    const total = items.reduce((s, i) => s + i.revenue, 0);
    const groups: Partial<Record<RevenueBusinessCategory, { revenue: number; quantity: number; items: typeof items }>> = {};

    items.forEach((item) => {
      const key = getRevenueBusinessCategory(item);
      if (!groups[key]) groups[key] = { revenue: 0, quantity: 0, items: [] };
      groups[key].revenue += item.revenue;
      groups[key].quantity += item.quantity;
      groups[key].items.push(item);
    });

    return Object.entries(groups)
      .map(([category, g]) => ({
        category: category as RevenueBusinessCategory,
        revenue: g.revenue,
        quantity: g.quantity,
        pct: total > 0 ? (g.revenue / total) * 100 : 0,
        averageTicket: g.quantity > 0 ? g.revenue / g.quantity : 0,
        items: [...g.items].sort((a, b) => b.revenue - a.revenue),
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [financial?.topItems]);

  const previousBillingRevenueByCategory = useMemo(() => {
    const groups: Partial<Record<RevenueBusinessCategory, number>> = {};
    (previousFinancial?.topItems ?? []).forEach((item) => {
      const key = getRevenueBusinessCategory(item);
      groups[key] = (groups[key] ?? 0) + item.revenue;
    });
    return groups;
  }, [previousFinancial?.topItems]);

  // ------------------------------------------------------------------
  // Explorer de Base — filter options + pagination
  // ------------------------------------------------------------------
  const explorerSpeciesOptions = useMemo(() => {
    const map: Record<string, string> = {};
    explorerRows.forEach((r) => {
      r.pets.forEach((p) => {
        if (p.speciesId && p.speciesName) map[p.speciesId] = p.speciesName;
      });
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [explorerRows]);

  const explorerBreedOptions = useMemo(() => {
    const map: Record<string, string> = {};
    explorerRows
      .filter((r) => explorerSpecies === "all" || r.pets.some((p) => p.speciesId === explorerSpecies))
      .forEach((r) => {
        r.pets
          .filter((p) => explorerSpecies === "all" || p.speciesId === explorerSpecies)
          .forEach((p) => {
            if (p.breedId && p.breedName) map[p.breedId] = p.breedName;
          });
      });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [explorerRows, explorerSpecies]);

  const explorerGenderOptions = useMemo(() => {
    const map: Record<string, string> = {};
    explorerRows.forEach((r) => {
      r.pets.forEach((p) => {
        if (p.genderId && p.genderName) map[p.genderId] = p.genderName;
      });
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [explorerRows]);

  const explorerSizeOptions = useMemo(() => {
    const map: Record<string, string> = {};
    explorerRows.forEach((r) => {
      r.pets.forEach((p) => {
        if (p.sizeCategoryId && p.sizeCategoryName) map[p.sizeCategoryId] = p.sizeCategoryName;
      });
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [explorerRows]);

  const explorerCoatTypeOptions = useMemo(() => {
    const map: Record<string, string> = {};
    explorerRows.forEach((r) => {
      r.pets.forEach((p) => {
        if (p.coatTypeId && p.coatTypeName) map[p.coatTypeId] = p.coatTypeName;
      });
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [explorerRows]);

  const paginationExplorer = useMemo(() => {
    let list: ExplorerRow[] = [...explorerRows];
    const ageMin = explorerAgeMin === "" ? null : Number(explorerAgeMin);
    const ageMax = explorerAgeMax === "" ? null : Number(explorerAgeMax);
    const minVisits = explorerMinVisits === "" ? null : Number(explorerMinVisits);
    const minTicket = explorerMinTicket === "" ? null : Number(explorerMinTicket);

    // Pet-level filters: an owner passes if ANY of their pets match all pet filters
    const hasPetFilter =
      explorerSpecies !== "all" ||
      explorerBreed !== "all" ||
      explorerGender !== "all" ||
      explorerAgeGroup !== "all" ||
      explorerDetailedAgeGroup !== "all" ||
      ageMin !== null ||
      ageMax !== null ||
      explorerSterilized !== "all" ||
      explorerWeightStatus !== "all" ||
      explorerSize !== "all" ||
      explorerCoatType !== "all" ||
      explorerPetStatus !== "all";
    if (hasPetFilter) {
      list = list.filter((r) =>
        r.pets.some((p) => {
          if (explorerSpecies !== "all" && p.speciesId !== explorerSpecies) return false;
          if (explorerBreed !== "all" && p.breedId !== explorerBreed) return false;
          if (explorerGender !== "all" && p.genderId !== explorerGender) return false;
          if (explorerAgeGroup !== "all" && p.ageGroup !== explorerAgeGroup) return false;
          if (explorerDetailedAgeGroup !== "all" && p.detailedAgeGroup !== explorerDetailedAgeGroup) return false;
          if (ageMin !== null && (p.ageYears === null || p.ageYears < ageMin)) return false;
          if (ageMax !== null && (p.ageYears === null || p.ageYears > ageMax)) return false;
          if (explorerSterilized === "yes" && p.isSterilized !== true) return false;
          if (explorerSterilized === "no" && p.isSterilized === true) return false;
          if (explorerWeightStatus === "with" && p.currentWeight === null) return false;
          if (explorerWeightStatus === "missing" && p.currentWeight !== null) return false;
          if (explorerWeightStatus === "stale" && (p.daysSinceWeightUpdate === null || p.daysSinceWeightUpdate < 180)) return false;
          if (explorerSize !== "all" && p.sizeCategoryId !== explorerSize) return false;
          if (explorerCoatType !== "all" && p.coatTypeId !== explorerCoatType) return false;
          if (explorerPetStatus === "alive" && !p.isAlive) return false;
          if (explorerPetStatus === "deceased" && p.isAlive) return false;
          return true;
        })
      );
    }

    if (explorerLastVisitDays !== "all") {
      if (explorerLastVisitDays === "never") {
        list = list.filter((r) => r.daysSinceLastVisit === null);
      } else {
        const minDays = Number(explorerLastVisitDays);
        list = list.filter((r) => r.daysSinceLastVisit === null || r.daysSinceLastVisit >= minDays);
      }
    }

    if (explorerAbc !== "all") list = list.filter((r) => r.abcCurve === explorerAbc);
    if (explorerRelationshipStatus !== "all") list = list.filter((r) => r.relationshipStatus === explorerRelationshipStatus);
    if (minVisits !== null) list = list.filter((r) => r.visitCount >= minVisits);
    if (minTicket !== null) list = list.filter((r) => r.averageTicket >= minTicket);
    if (explorerHasPhone === "yes") list = list.filter((r) => r.hasPhone);
    if (explorerHasPhone === "no") list = list.filter((r) => !r.hasPhone);
    if (explorerHasWhatsApp === "yes") list = list.filter((r) => r.hasWhatsApp);
    if (explorerHasWhatsApp === "no") list = list.filter((r) => !r.hasWhatsApp);

    const s = explorerSearch.trim().toLowerCase();
    if (s) {
      list = list.filter(
        (r) =>
          r.ownerName.toLowerCase().includes(s) ||
          r.pets.some((p) => p.petName.toLowerCase().includes(s))
      );
    }

    const total = list.length;
    const pages = Math.ceil(total / PAGE_SIZE) || 1;
    const start = (pageExplorer - 1) * PAGE_SIZE;
    return { items: list.slice(start, start + PAGE_SIZE), total, pages, fullList: list };
  }, [
    explorerRows, explorerSpecies, explorerBreed, explorerGender, explorerAgeGroup, explorerDetailedAgeGroup, explorerAgeMin, explorerAgeMax, explorerSterilized, explorerWeightStatus, explorerSize, explorerCoatType, explorerPetStatus, explorerLastVisitDays,
    explorerAbc, explorerRelationshipStatus, explorerMinVisits, explorerMinTicket, explorerHasPhone, explorerHasWhatsApp, explorerSearch, pageExplorer,
  ]);

  // ------------------------------------------------------------------
  // Financial table pagination helpers
  // ------------------------------------------------------------------
  const paginationProducts = useMemo(() => {
    let list = [...(financial?.topItems ?? [])];
    if (filterProductsType !== "all") list = list.filter((r) => r.type === filterProductsType);
    const s = filterProductsSearch.trim().toLowerCase();
    if (s) list = list.filter((r) => r.itemName.toLowerCase().includes(s));
    const [field, dir] = sortProducts.split("_") as [string, "asc" | "desc"];
    list.sort((a, b) => {
      let cmp = 0;
      if (field === "itemName") cmp = a.itemName.localeCompare(b.itemName, numberLocale);
      else if (field === "type") cmp = (a.type ?? "").localeCompare(b.type ?? "", numberLocale);
      else if (field === "quantity") cmp = a.quantity - b.quantity;
      else cmp = a.revenue - b.revenue;
      return dir === "desc" ? -cmp : cmp;
    });
    const total = list.length;
    const pages = Math.ceil(total / PAGE_SIZE) || 1;
    const start = (pageProducts - 1) * PAGE_SIZE;
    return { items: list.slice(start, start + PAGE_SIZE), total, pages, fullList: list };
  }, [financial?.topItems, filterProductsType, filterProductsSearch, sortProducts, pageProducts, numberLocale]);

  const paginationOwners = useMemo(() => {
    let list = [...ownersWithAbc];
    if (filterOwnersAbc !== "all") list = list.filter((o) => o.abcCurve === filterOwnersAbc);
    if (filterOwnersSpecies !== "all")
      list = list.filter((o) => o.speciesIds.includes(filterOwnersSpecies));
    if (filterOwnersBreed !== "all")
      list = list.filter((o) => o.breedIds.includes(filterOwnersBreed));
    const s = filterOwnersSearch.trim().toLowerCase();
    if (s) list = list.filter((o) => o.ownerName.toLowerCase().includes(s));
    const [field, dir] = sortOwners.split("_") as [string, "asc" | "desc"];
    list.sort((a, b) => {
      let cmp = 0;
      if (field === "ownerName") cmp = a.ownerName.localeCompare(b.ownerName, numberLocale);
      else if (field === "saleCount") cmp = a.saleCount - b.saleCount;
      else cmp = a.totalSpent - b.totalSpent;
      return dir === "desc" ? -cmp : cmp;
    });
    const total = list.length;
    const pages = Math.ceil(total / PAGE_SIZE) || 1;
    const start = (pageOwners - 1) * PAGE_SIZE;
    return { items: list.slice(start, start + PAGE_SIZE), total, pages, fullList: list };
  }, [ownersWithAbc, filterOwnersAbc, filterOwnersSpecies, filterOwnersBreed, filterOwnersSearch, sortOwners, pageOwners, numberLocale]);

  const stockRevenueByItemId = useMemo(() => {
    const map: Record<string, number> = {};
    (stockFinancial?.topItems ?? []).forEach((item) => { map[item.itemId ?? ""] = item.revenue; });
    return map;
  }, [stockFinancial?.topItems]);

  const paginationOutflow = useMemo(() => {
    let list = [...(stock?.movementRows ?? [])];
    const s = filterOutflowSearch.trim().toLowerCase();
    if (s) list = list.filter((r) => r.itemName.toLowerCase().includes(s));
    const [field, dir] = sortOutflow.split("_") as [string, "asc" | "desc"];
    list.sort((a, b) => {
      let cmp = 0;
      if (field === "itemName") cmp = a.itemName.localeCompare(b.itemName, numberLocale);
      else if (field === "revenue") cmp = (stockRevenueByItemId[a.itemId] ?? 0) - (stockRevenueByItemId[b.itemId] ?? 0);
      else cmp = a.quantityOut - b.quantityOut;
      return dir === "desc" ? -cmp : cmp;
    });
    const total = list.length;
    const pages = Math.ceil(total / PAGE_SIZE) || 1;
    const start = (pageOutflow - 1) * PAGE_SIZE;
    return { items: list.slice(start, start + PAGE_SIZE), total, pages, fullList: list };
  }, [stock?.movementRows, stockRevenueByItemId, filterOutflowSearch, sortOutflow, pageOutflow, numberLocale]);

  const itemRankingsByType = useMemo(() => {
    const buildRanking = (type: "PRODUTO" | "SERVICO" | "PACOTE") => {
      const rows = (stockFinancial?.topItems ?? [])
        .filter((item) => item.type === type)
        .sort((a, b) => b.quantity - a.quantity || b.revenue - a.revenue)
        .slice(0, 5);
      const totalQuantity = rows.reduce((sum, item) => sum + item.quantity, 0);
      const totalRevenue = rows.reduce((sum, item) => sum + item.revenue, 0);
      const maxQuantity = Math.max(...rows.map((item) => item.quantity), 0);
      return { rows, totalQuantity, totalRevenue, maxQuantity };
    };

    return {
      products: buildRanking("PRODUTO"),
      services: buildRanking("SERVICO"),
      packages: buildRanking("PACOTE"),
    };
  }, [stockFinancial?.topItems]);

  const paginationInactive = useMemo(() => {
    let list = [...inactiveOwners];
    const s = inactiveSearch.trim().toLowerCase();
    if (s) list = list.filter((o) => o.ownerName.toLowerCase().includes(s));
    const total = list.length;
    const pages = Math.ceil(total / PAGE_SIZE) || 1;
    const start = (pageInactive - 1) * PAGE_SIZE;
    return { items: list.slice(start, start + PAGE_SIZE), total, pages, fullList: list };
  }, [inactiveOwners, inactiveSearch, pageInactive]);

  const campaignPeriodSummary = useMemo(() => {
    if (!dateFrom || !dateTo) {
      return { count: 0, sent: 0, read: 0, revenue: 0, cost: 0, roi: null as number | null };
    }
    const summary = campaigns
      .filter((campaign) => {
        const timestamp = campaign.dispatchedAt ?? campaign.scheduledAt ?? campaign.createdAt;
        if (!timestamp) return false;
        const date = new Date(timestamp);
        return date >= dateFrom && date <= dateTo;
      })
      .reduce(
        (summary, campaign) => ({
          count: summary.count + 1,
          sent: summary.sent + campaign.totalSent,
          read: summary.read + campaign.totalRead,
          revenue: summary.revenue + campaign.attributedRevenue,
          cost: summary.cost + Number(campaign.estimatedCostBrl ?? 0),
        }),
        { count: 0, sent: 0, read: 0, revenue: 0, cost: 0 },
      );
    return {
      ...summary,
      roi: summary.cost > 0 ? (summary.revenue - summary.cost) / summary.cost : null,
    };
  }, [campaigns, dateFrom, dateTo]);

  const explorerSmartViewSummary = useMemo(() => {
    const clientsA = explorerRows.filter((row) => row.abcCurve === "A");
    const vipDormant = clientsA.filter((row) => row.daysSinceLastVisit === null || row.daysSinceLastVisit >= 90);
    const potentialClients = explorerRows.filter((row) => row.abcCurve === "B");
    const puppies = explorerRows.filter((row) => row.pets.some((pet) => pet.isAlive && pet.detailedAgeGroup === "puppy"));
    const seniors = explorerRows.filter((row) => row.pets.some((pet) => pet.isAlive && pet.detailedAgeGroup === "senior"));
    const geriatric = explorerRows.filter((row) => row.pets.some((pet) => pet.isAlive && pet.detailedAgeGroup === "geriatric"));
    const highTicket = explorerRows.filter((row) => row.averageTicket >= 300);
    const lowRecurrence = explorerRows.filter((row) => row.visitCount <= 1 && (row.daysSinceLastVisit ?? 999) >= 90);
    const staleWeight = explorerRows.filter((row) =>
      row.pets.some((pet) => pet.isAlive && (pet.daysSinceWeightUpdate === null || pet.daysSinceWeightUpdate >= 180))
    );

    return {
      clientsA: clientsA.length,
      vipDormant: vipDormant.length,
      potentialClients: potentialClients.length,
      puppies: puppies.length,
      seniors: seniors.length,
      geriatric: geriatric.length,
      highTicket: highTicket.length,
      lowRecurrence: lowRecurrence.length,
      staleWeight: staleWeight.length,
    };
  }, [explorerRows]);

  const periodLabel = useMemo(() => {
    if (!dateFrom || !dateTo) return "";
    if (isSameDay(dateFrom, dateTo)) return format(dateFrom, "dd/MM/yyyy", { locale: dateLocale });
    return `${format(dateFrom, "dd/MM/yyyy", { locale: dateLocale })} – ${format(dateTo, "dd/MM/yyyy", { locale: dateLocale })}`;
  }, [dateFrom, dateTo, dateLocale]);

  const professionalRows = useMemo(
    () => byProfessional.filter((row) => row.totalAppointments > 0),
    [byProfessional],
  );

  const professionalsByRevenue = useMemo(
    () => [...professionalRows].sort((a, b) => b.receita - a.receita),
    [professionalRows],
  );

  const professionalsByAttendances = useMemo(
    () => [...professionalRows].sort((a, b) => b.totalAppointments - a.totalAppointments),
    [professionalRows],
  );

  const professionalSummary = useMemo(() => {
    const totalAppointments = professionalRows.reduce((sum, row) => sum + row.totalAppointments, 0);
    const revenue = professionalRows.reduce((sum, row) => sum + row.receita, 0);
    const settledWeighted = professionalRows.reduce((sum, row) => sum + row.pctPagos * row.totalAppointments, 0);
    const finishedWeighted = professionalRows.reduce((sum, row) => sum + row.pctFinalizados * row.totalAppointments, 0);
    const activeProfessionals = professionalRows.length;

    return {
      activeProfessionals,
      totalAppointments,
      revenue,
      averageTicket: totalAppointments > 0 ? revenue / totalAppointments : 0,
      settledPct: totalAppointments > 0 ? settledWeighted / totalAppointments : 0,
      finishedPct: totalAppointments > 0 ? finishedWeighted / totalAppointments : 0,
      topProfessional: professionalsByRevenue[0] ?? null,
      maxRevenue: Math.max(...professionalRows.map((row) => row.receita), 0),
    };
  }, [professionalRows, professionalsByRevenue]);

  const selectedProfessional = useMemo(
    () => professionalRows.find((row) => (row.vetUserId ?? "null") === selectedProfessionalId) ?? null,
    [professionalRows, selectedProfessionalId],
  );

  const professionalTabRows = useMemo(
    () => byProfessionalTab.filter((row) => row.totalAppointments > 0),
    [byProfessionalTab],
  );

  const professionalTabSummary = useMemo(() => {
    const totalAppointments = professionalTabRows.reduce((sum, row) => sum + row.totalAppointments, 0);
    const totalRevenue = professionalTabRows.reduce((sum, row) => sum + row.receita, 0);
    const totalCommission = professionalTabRows.reduce((sum, row) => sum + row.commission, 0);
    const activeProfessionals = professionalTabRows.length;
    return { activeProfessionals, totalAppointments, totalRevenue, totalCommission };
  }, [professionalTabRows]);

  // Aggregate appointment types across all professionals for the types panel
  const aggregatedAppointmentTypes = useMemo(() => {
    const map = new Map<string, import("@/hooks/useReportsByProfessional").ProfessionalAppointmentTypeBreakdown>();
    professionalRows.forEach((row) => {
      row.appointmentTypes.forEach((typeRow) => {
        const existing = map.get(typeRow.type);
        if (!existing) {
          map.set(typeRow.type, { ...typeRow });
        } else {
          existing.totalAppointments += typeRow.totalAppointments;
          existing.confirmados += typeRow.confirmados;
          existing.finalizados += typeRow.finalizados;
          existing.pagos += typeRow.pagos;
          existing.receita += typeRow.receita;
          existing.color = existing.color ?? typeRow.color;
          existing.icon = existing.icon ?? typeRow.icon;
        }
      });
    });
    return Array.from(map.values()).map((row) => ({
      ...row,
      ticketMedio: row.totalAppointments > 0 ? row.receita / row.totalAppointments : 0,
      pctFinalizados: row.totalAppointments > 0 ? (row.finalizados / row.totalAppointments) * 100 : 0,
      pctPagos: row.totalAppointments > 0 ? (row.pagos / row.totalAppointments) * 100 : 0,
    }));
  }, [professionalRows]);

  const professionalAppointmentTypes = useMemo(() => {
    const totals = new Map<string, number>();
    professionalRows.forEach((row) => {
      row.appointmentTypes.forEach((typeRow) => {
        totals.set(typeRow.type, (totals.get(typeRow.type) ?? 0) + typeRow.totalAppointments);
      });
    });
    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type]) => type);
  }, [professionalRows]);

  const professionalAppointmentTypeLabels = useMemo(() => {
    const labels = new Map<string, string>();
    professionalRows.forEach((row) => {
      row.appointmentTypes.forEach((typeRow) => {
        if (!labels.has(typeRow.type)) {
          labels.set(typeRow.type, typeRow.typeLabel);
        }
      });
    });
    return labels;
  }, [professionalRows]);

  const getProfessionalAppointmentTypeLabel = useCallback(
    (type: string) => professionalAppointmentTypeLabels.get(type) ?? getAppointmentTypeLabel(type),
    [getAppointmentTypeLabel, professionalAppointmentTypeLabels],
  );

  const professionalMixRows = useMemo(
    () =>
      professionalsByRevenue.slice(0, 10).map((row) => {
        const typeCounts = Object.fromEntries(
          professionalAppointmentTypes.map((type) => [
            getAppointmentTypeDataKey(type),
            row.appointmentTypes.find((typeRow) => typeRow.type === type)?.totalAppointments ?? 0,
          ]),
        );

        return {
          ...typeCounts,
          rowId: row.vetUserId ?? "null",
          professionalName: row.professionalName,
          receita: row.receita,
          totalAppointments: row.totalAppointments,
          ticketMedio: row.totalAppointments > 0 ? row.receita / row.totalAppointments : 0,
          pctPagos: row.pctPagos,
        };
      }),
    [professionalAppointmentTypes, professionalsByRevenue],
  );

  const appointmentFunnelRows = useMemo(
    () =>
      (overview?.appointmentsBar ?? []).map((row) => ({
        ...row,
        atendimentos: row.agendados,
      })),
    [overview?.appointmentsBar],
  );

  // ------------------------------------------------------------------
  // XLSX exports
  // ------------------------------------------------------------------
  const handleExportOwners = () => {
    exportToExcel({
      fileName: `tutores_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("patients"),
      rows: paginationOwners.fullList.map((o) => ({
        [t("owner")]: o.ownerName,
        [t("abcCurve")]: o.abcCurve ?? "",
        [t("totalSpent")]: o.totalSpent,
        [t("sales")]: o.saleCount,
        [t("species")]: o.speciesNames.join(", "),
        [t("breed")]: o.breedNames.join(", "),
      })),
    });
  };

  const handleExportInactive = () => {
    exportToExcel({
      fileName: `clientes_dormentes_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("dormantClients"),
      rows: paginationInactive.fullList.map((o) => ({
        [t("owner")]: o.ownerName,
        [t("lastVisit")]: formatDate(o.lastAppointmentDate),
        [t("daysSinceLastVisit")]: o.daysSinceLastVisit,
        [t("totalHistoricalSpent")]: o.totalHistoricalSpent,
        [t("pets")]: o.petCount,
        [t("species")]: o.speciesNames.join(", "),
      })),
    });
  };

  const handleExportExplorer = () => {
    exportToExcel({
      fileName: `explorer_base_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("tabExplorer"),
      rows: paginationExplorer.fullList.flatMap((r) =>
        r.pets.length > 0
          ? r.pets.map((p) => ({
              [t("owner")]: r.ownerName,
              [t("abcCurve")]: r.abcCurve === "none" ? "" : r.abcCurve,
              [t("totalHistoricalSpent")]: r.totalHistoricalSpent,
              [t("lastVisit")]: r.lastVisitDate ? formatDate(r.lastVisitDate) : "",
              [t("pet")]: p.petName,
              [t("species")]: p.speciesName ?? "",
              [t("breed")]: p.breedName ?? "",
              [t("explorerSize")]: p.sizeCategoryName ?? "",
              [t("explorerCoatType")]: p.coatTypeName ?? "",
              [t("explorerGender")]: p.genderName ?? "",
              [t("explorerPhone")]: r.primaryPhone ?? "",
              [t("explorerWhatsApp")]: r.hasWhatsApp ? t("yes") : t("no"),
            }))
          : [{
              [t("owner")]: r.ownerName,
              [t("abcCurve")]: r.abcCurve === "none" ? "" : r.abcCurve,
              [t("totalHistoricalSpent")]: r.totalHistoricalSpent,
              [t("lastVisit")]: r.lastVisitDate ? formatDate(r.lastVisitDate) : "",
              [t("pet")]: "",
              [t("species")]: "",
              [t("breed")]: "",
              [t("explorerSize")]: "",
              [t("explorerCoatType")]: "",
              [t("explorerGender")]: "",
              [t("explorerPhone")]: r.primaryPhone ?? "",
              [t("explorerWhatsApp")]: r.hasWhatsApp ? t("yes") : t("no"),
            }]
      ),
    });
  };

  const handleExportOverdueVaccines = () => {
    exportToExcel({
      fileName: `aplicacoes_atrasadas_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("overdueVaccinesTitle"),
      rows: overdueVaccines.map((row) => ({
        [t("owner")]: row.ownerName,
        [t("pet")]: row.petName,
        [t("species")]: row.speciesName ?? "",
        [t("application")]: row.itemName,
        [t("dueDate")]: formatDate(row.dueDate),
        [t("daysOverdue")]: row.daysOverdue,
      })),
    });
  };

  const handleExportUpcomingApplications = () => {
    exportToExcel({
      fileName: `aplicacoes_proximas_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("upcomingApplicationsTitle"),
      rows: upcomingApplications.map((row) => ({
        [t("owner")]: row.ownerName,
        [t("pet")]: row.petName,
        [t("species")]: row.speciesName ?? "",
        [t("application")]: row.itemName,
        [t("dueDate")]: formatDate(row.dueDate),
        [t("daysUntilDue")]: row.daysUntilDue,
      })),
    });
  };

  const handleExportAppointmentKpis = () => {
    if (!overview) return;
    exportToExcel({
      fileName: `atendimentos_indicadores_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("tabByProfessional"),
      rows: [
        { [t("period")]: periodLabel, [t("metric")]: t("scheduled"), [t("value")]: overview.kpis.agendados },
        { [t("period")]: periodLabel, [t("metric")]: t("confirmed"), [t("value")]: overview.kpis.confirmados },
        { [t("period")]: periodLabel, [t("metric")]: t("finished"), [t("value")]: overview.kpis.finalizados },
        { [t("period")]: periodLabel, [t("metric")]: t("settled"), [t("value")]: overview.kpis.pagos },
        { [t("period")]: periodLabel, [t("metric")]: t("totalRevenue"), [t("value")]: overview.kpis.receitaTotal },
      ],
    });
  };

  const handleExportAppointmentFunnel = () => {
    exportToExcel({
      fileName: `funil_atendimentos_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("funnelTitle"),
      rows: appointmentFunnelRows.map((row) => ({
        [t("period")]: row.label,
        [t("attendances")]: row.atendimentos,
        [t("settled")]: row.pagos,
      })),
    });
  };

  const handleExportProfessionalsTab = () => {
    const rows = [...professionalTabRows].sort((a, b) => b.receita - a.receita);
    const totalRevenue = professionalTabSummary.totalRevenue;

    const summaryRows = rows.map((row) => ({
      [t("professional")]: row.professionalName,
      [t("attendances")]: row.totalAppointments,
      [t("revenue")]: row.receita,
      [t("commission")]: row.commission,
      [t("profExportTicket")]: row.totalAppointments > 0 ? +(row.receita / row.totalAppointments).toFixed(2) : 0,
      [t("profExportFinishedPct")]: `${row.pctFinalizados.toFixed(1)}%`,
      [t("profExportPaidPct")]: `${row.pctPagos.toFixed(1)}%`,
      ["% " + t("revenue")]: totalRevenue > 0 ? `${((row.receita / totalRevenue) * 100).toFixed(1)}%` : "0%",
    }));

    const typeRows = rows.flatMap((row) =>
      row.appointmentTypes.map((tp) => ({
        [t("professional")]: row.professionalName,
        [t("type")]: tp.typeLabel,
        [t("attendances")]: tp.totalAppointments,
        [t("revenue")]: tp.receita,
        [t("commission")]: tp.commission,
        [t("profExportTicket")]: tp.totalAppointments > 0 ? +(tp.receita / tp.totalAppointments).toFixed(2) : 0,
        [t("profExportFinishedPct")]: `${tp.pctFinalizados.toFixed(1)}%`,
        [t("profExportPaidPct")]: `${tp.pctPagos.toFixed(1)}%`,
      })),
    );

    const serviceRows = rows.flatMap((row) =>
      row.services.map((svc) => ({
        [t("professional")]: row.professionalName,
        [t("profExportItemName")]: svc.itemName,
        [t("profExportQty")]: svc.quantity,
        [t("profExportAppointmentCount")]: svc.appointmentCount,
        [t("revenue")]: +svc.revenue.toFixed(2),
      })),
    );

    const productRows = rows.flatMap((row) =>
      row.products.map((prod) => ({
        [t("professional")]: row.professionalName,
        [t("profExportItemName")]: prod.itemName,
        [t("profExportQty")]: prod.quantity,
        [t("profExportAppointmentCount")]: prod.appointmentCount,
        [t("revenue")]: +prod.revenue.toFixed(2),
      })),
    );

    const patientRows = rows.flatMap((row) =>
      row.patients.map((p) => ({
        [t("professional")]: row.professionalName,
        [t("profExportPetName")]: p.petName,
        [t("profExportOwnerName")]: p.ownerName ?? "",
        [t("profExportSpecies")]: p.speciesName ?? "",
        [t("profExportAppointmentCount")]: p.appointmentCount,
        [t("revenue")]: +p.revenue.toFixed(2),
      })),
    );

    const commissionRows = rows.flatMap((row) =>
      [...row.services, ...row.products]
        .filter((item) => item.commission > 0)
        .map((item) => ({
          [t("professional")]: row.professionalName,
          [t("profExportItemName")]: item.itemName,
          [t("profExportQty")]: item.quantity,
          [t("revenue")]: +item.revenue.toFixed(2),
          [t("commission")]: +item.commission.toFixed(2),
          [t("profExportCommissionPct")]: item.revenue > 0 ? `${((item.commission / item.revenue) * 100).toFixed(1)}%` : "0%",
        })),
    );

    exportToExcelMultiSheet({
      fileName: `${t("profExportFileName")}_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheets: [
        { sheetName: t("profExportSummarySheet"), rows: summaryRows },
        { sheetName: t("profExportDetailSheet"), rows: typeRows },
        { sheetName: t("profExportServicesSheet"), rows: serviceRows },
        { sheetName: t("profExportProductsSheet"), rows: productRows },
        { sheetName: t("profExportPatientsSheet"), rows: patientRows },
        { sheetName: t("profExportCommissionsSheet"), rows: commissionRows },
      ],
    });
  };

  const handleExportFinancialKpis = () => {
    if (!financial) return;
    exportToExcel({
      fileName: `receita_indicadores_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("tabFinancial"),
      rows: [
        { [t("period")]: periodLabel, [t("metric")]: t("revenueByService"), [t("value")]: financial.revenueByService },
        { [t("period")]: periodLabel, [t("metric")]: t("revenueByProduct"), [t("value")]: financial.revenueByProduct },
        { [t("period")]: periodLabel, [t("metric")]: t("totalRevenue"), [t("value")]: financial.totalRevenue },
        { [t("period")]: periodLabel, [t("metric")]: t("averageTicket"), [t("value")]: financial.ticketMedio },
      ],
    });
  };

  const handleExportRevenueByPeriod = () => {
    exportToExcel({
      fileName: `receita_periodo_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("revenueByPeriod"),
      rows: (overview?.revenueLine ?? []).map((row) => ({
        [t("period")]: row.label,
        [t("revenue")]: row.receita,
      })),
    });
  };

  const handleExportBillingByCategory = () => {
    exportToExcel({
      fileName: `faturamento_categoria_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("billingTitle"),
      rows: billingByCategory.flatMap((group) =>
        group.items.map((item) => ({
          [t("category")]: t(`revenueCategory.${group.category}`),
          [t("item")]: item.itemName,
          [t("type")]: item.type,
          [t("qty")]: item.quantity,
          [t("revenue")]: item.revenue,
          [t("ofTotal")]: financial?.totalRevenue ? (item.revenue / financial.totalRevenue) * 100 : 0,
        })),
      ),
    });
  };

  const handleExportBilledItems = () => {
    exportToExcel({
      fileName: `itens_faturados_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("billedItems"),
      rows: paginationProducts.fullList.map((row) => ({
        [t("item")]: row.itemName,
        [t("type")]: row.type,
        [t("qty")]: row.quantity,
        [t("revenue")]: row.revenue,
      })),
    });
  };

  const handleExportStock = () => {
    exportToExcel({
      fileName: `estoque_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("stockTitle"),
      rows: (stock?.movementRows ?? []).map((row) => ({
        [t("item")]: row.itemName,
        [t("stockEntries")]: row.quantityIn,
        [t("stockExits")]: row.quantityOut,
        [t("revenue")]: stockRevenueByItemId[row.itemId] ?? 0,
        [t("stockCurrentQty")]: stock?.currentStockByItemId[row.itemId] ?? "",
      })),
    });
  };

  const handleExportExecutiveOpportunities = () => {
    exportToExcel({
      fileName: `oportunidades_${format(new Date(), "yyyyMMdd")}.xlsx`,
      sheetName: t("executiveOpportunitiesTitle"),
      rows: [
        { [t("metric")]: t("dormantClients"), [t("value")]: inactiveOwners.length },
        { [t("metric")]: t("overdueVaccinesTitle"), [t("value")]: overdueVaccines.length },
        { [t("metric")]: t("upcomingApplicationsTitle"), [t("value")]: upcomingApplications.length },
        { [t("metric")]: t("campaignsInPeriod"), [t("value")]: campaignPeriodSummary.count },
      ],
    });
  };

  const applyExplorerSmartView = (
    view:
      | "clientsA"
      | "vipDormant"
      | "potential"
      | "puppies"
      | "seniors"
      | "geriatric"
      | "highTicket"
      | "lowRecurrence"
      | "staleWeight"
  ) => {
    setExplorerSearch("");
    setExplorerSpecies("all");
    setExplorerBreed("all");
    setExplorerGender("all");
    setExplorerSize("all");
    setExplorerCoatType("all");
    setExplorerPetStatus("alive");
    setExplorerHasPhone("all");
    setExplorerHasWhatsApp("all");
    setExplorerAgeMin("");
    setExplorerAgeMax("");
    setExplorerDetailedAgeGroup("all");
    setExplorerSterilized("all");
    setExplorerRelationshipStatus("all");
    setExplorerMinVisits("");
    setExplorerMinTicket("");
    setExplorerWeightStatus("all");
    setExplorerLastVisitDays("all");
    setExplorerAgeGroup("all");
    setExplorerAbc("all");

    if (view === "clientsA") {
      setExplorerPetStatus("all");
      setExplorerAbc("A");
    }
    if (view === "vipDormant") {
      setExplorerPetStatus("all");
      setExplorerAbc("A");
      setExplorerLastVisitDays("90");
    }
    if (view === "potential") {
      setExplorerPetStatus("all");
      setExplorerAbc("B");
    }
    if (view === "puppies") {
      setExplorerDetailedAgeGroup("puppy");
    }
    if (view === "seniors") {
      setExplorerDetailedAgeGroup("senior");
    }
    if (view === "geriatric") {
      setExplorerDetailedAgeGroup("geriatric");
    }
    if (view === "highTicket") {
      setExplorerMinTicket("300");
    }
    if (view === "lowRecurrence") {
      setExplorerMinVisits("1");
      setExplorerLastVisitDays("90");
    }
    if (view === "staleWeight") {
      setExplorerWeightStatus("stale");
    }

    setPageExplorer(1);
  };

  // ------------------------------------------------------------------
  // Pagination helper
  // ------------------------------------------------------------------
  function PaginationBar({
    page,
    pages,
    total,
    setPage,
  }: {
    page: number;
    pages: number;
    total: number;
    setPage: (p: number) => void;
  }) {
    if (pages <= 1) return null;
    return (
      <div className="flex flex-col sm:flex-row gap-2 items-center justify-between px-0 pt-4 border-t mt-4">
        <p className="text-sm text-muted-foreground">
          {t("showing")} {(page - 1) * PAGE_SIZE + 1} {t("to")} {Math.min(page * PAGE_SIZE, total)}{" "}
          {t("of")} {total} {t("results")}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
            <ChevronLeft className="h-4 w-4" />
            {t("previous")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(pages, page + 1))}
            disabled={page >= pages}
          >
            {t("next")}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  function ItemRankingCard({
    title,
    description,
    ranking,
    badgeLabel,
  }: {
    title: string;
    description: string;
    ranking: {
      rows: Array<{ itemId: string | null; itemName: string; quantity: number; revenue: number }>;
      totalQuantity: number;
      totalRevenue: number;
      maxQuantity: number;
    };
    badgeLabel: string;
  }) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
            <Badge variant="secondary" className="shrink-0">{badgeLabel}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loadingStockFinancial ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </div>
          ) : ranking.rows.length === 0 ? (
            <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t("emptyBreakdown")}
            </p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">{t("quantitySold")}</p>
                  <p className="text-xl font-semibold tabular-nums">{ranking.totalQuantity}</p>
                </div>
                <div className="rounded-lg bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">{t("accumulatedValue")}</p>
                  <p className="text-xl font-semibold tabular-nums">{formatCurrency(ranking.totalRevenue)}</p>
                </div>
              </div>

              {ranking.rows.map((item, index) => {
                const quantityPct = ranking.maxQuantity > 0 ? (item.quantity / ranking.maxQuantity) * 100 : 0;
                return (
                  <div key={item.itemId ?? `${item.itemName}-${index}`} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant={index === 0 ? "default" : "secondary"} className="h-5 min-w-5 justify-center px-1.5">
                            {index + 1}
                          </Badge>
                          <p className="truncate text-sm font-medium">{item.itemName}</p>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {item.quantity} {t("qty").toLowerCase()} · {formatCurrency(item.revenue)}
                        </p>
                      </div>
                      <p className="shrink-0 text-sm font-semibold">
                        {ranking.totalRevenue > 0 ? `${((item.revenue / ranking.totalRevenue) * 100).toFixed(1)}%` : "—"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ------------------------------------------------------------------
  // Render guards
  // ------------------------------------------------------------------
  if (isLoadingPermissions || permissionsMap === undefined) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-44" />
        <Skeleton className="h-10 w-full md:w-96 rounded-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canViewReports && !canViewApplications) {
    return (
      <div className="space-y-6">
        <h1 className="hidden md:block text-3xl font-bold text-foreground">{t("title")}</h1>
        <Alert variant="danger">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t("accessDenied")}</AlertTitle>
          <AlertDescription>{t("accessDeniedDescription")}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------
  return (
    <div className="space-y-4">
      <h1 className="hidden md:block text-3xl font-bold text-foreground">{t("title")}</h1>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          navigate(reportPathForTab(v as ReportsTabValue), { replace: true })
        }
      >
        {/* Tab bar */}
        <div className="flex flex-wrap items-center justify-between">
          <div className="w-full md:w-fit">
            <SegmentedTabs
              className="w-full"
              value={activeTab}
              onValueChange={(v) =>
                navigate(reportPathForTab(v as ReportsTabValue), { replace: true })
              }
              variant="outline"
              items={[
                ...(canViewReports
                  ? [
                      { value: "professional", label: t("tabByProfessional") },
                      ...(canViewFinancialReports
                        ? [
                            { value: "financial", label: t("tabFinancial") },
                            { value: "professionals", label: t("tabProfessionals") },
                            { value: "salesExplorer", label: t("salesExplorerTabLabel") },
                          ]
                        : []),
                      ...(canViewStockReports ? [{ value: "stock", label: t("tabStock") }] : []),
                      ...(canViewPlans ? [{ value: "plans", label: t(getStayReportTabKey(daycareHotelAccess.mode)) }] : []),
                      { value: "prescriptions", label: t("mapaRxTabLabel") },
                    ]
                  : []),
                ...(canViewApplications
                  ? [{ value: "applications", label: t("tabApplications") }]
                  : []),
              ]}
            />
          </div>

          {showPeriodSelect && (
            <Select value={period} onValueChange={(v) => setPeriod(v as PeriodValue)}>
              <SelectTrigger className="md:w-[180px] w-full">
                <SelectValue placeholder={t("period")} />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* ================================================================
            STAY PLANS
        ================================================================ */}
        {canViewPlans && (
          <TabsContent value="plans" className="space-y-5">
            <PlansTab show="all" />
          </TabsContent>
        )}

        {canViewReports && (
          <TabsContent value="prescriptions" className="space-y-5">
            <PrescriptionsReportTab />
          </TabsContent>
        )}

        {/* ================================================================
            BY PROFESSIONAL (Atendimentos) — redesigned
        ================================================================ */}
        {canViewReports && (
          <TabsContent value="professional" className="space-y-5">
            {/* Period Hero */}
            <AppointmentsPeriodHero
              dateFrom={proDateFrom}
              dateTo={proDateTo}
              compareMode={compareMode}
              totalAppointments={professionalSummary.totalAppointments}
              activeProfessionals={professionalSummary.activeProfessionals}
              onDateFromChange={setProDateFrom}
              onDateToChange={setProDateTo}
              onCompareModeChange={setCompareMode}
            />

            {/* KPI Cards */}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {(() => {
                const kpis = overview?.kpis;
                const cmpKpis = comparisonOverview?.kpis;
                const totalApts = kpis?.agendados ?? 0;
                const cmpApts = cmpKpis?.agendados ?? 0;
                const revenue = kpis?.receitaTotal ?? 0;
                const cmpRevenue = cmpKpis?.receitaTotal ?? 0;
                const ticket = totalApts > 0 ? revenue / totalApts : 0;
                const cmpTicket = (cmpKpis?.agendados ?? 0) > 0 ? cmpRevenue / cmpKpis!.agendados : 0;
                const conclusionPct = totalApts > 0 ? ((kpis?.finalizados ?? 0) / totalApts) * 100 : 0;
                const cmpConclusionPct = cmpApts > 0 ? ((cmpKpis?.finalizados ?? 0) / cmpApts) * 100 : 0;

                const deltaPct = (curr: number, prev: number) =>
                  prev > 0 ? ((curr - prev) / prev) * 100 : 0;

                const cards = [
                  {
                    label: t("attendances"),
                    value: String(totalApts),
                    cmpValue: String(cmpApts),
                    delta: deltaPct(totalApts, cmpApts),
                    icon: <Stethoscope className="h-4 w-4 text-primary" />,
                  },
                  {
                    label: t("totalRevenue"),
                    value: formatCurrency(revenue),
                    cmpValue: formatCurrency(cmpRevenue),
                    delta: deltaPct(revenue, cmpRevenue),
                    icon: <Receipt className="h-4 w-4 text-primary" />,
                  },
                  {
                    label: t("averageTicket"),
                    value: formatCurrency(ticket),
                    cmpValue: formatCurrency(cmpTicket),
                    delta: deltaPct(ticket, cmpTicket),
                    icon: <Activity className="h-4 w-4 text-primary" />,
                  },
                  {
                    label: t("finishedAndSettled"),
                    value: `${conclusionPct.toFixed(1)}%`,
                    cmpValue: `${cmpConclusionPct.toFixed(1)}%`,
                    delta: deltaPct(conclusionPct, cmpConclusionPct),
                    icon: <CheckCircle2 className="h-4 w-4 text-primary" />,
                  },
                ];

                return cards.map((card) => (
                  <Card key={card.label}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {card.label}
                      </CardTitle>
                      {card.icon}
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {loadingOverview ? (
                        <Skeleton className="h-8 w-28" />
                      ) : (
                        <div className="text-3xl font-bold tracking-tight">{card.value}</div>
                      )}
                      <div className="flex items-center gap-2">
                        {compareMode !== "none" && !loadingOverview && (
                          <>
                            <DeltaBadge pct={card.delta} />
                            <span className="text-xs text-muted-foreground">
                              {t("prevPeriod")} · <span className="font-medium">{card.cmpValue}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>

            {/* Volume diário + Heatmap */}
            <div className="grid gap-5 xl:grid-cols-2">
              <AppointmentsDailyVolumeChart
                currentData={overview?.appointmentsBar ?? []}
                comparisonData={compareMode !== "none" ? comparisonOverview?.appointmentsBar ?? [] : []}
                isLoading={loadingOverview}
                currentLabel={t("attendances")}
                comparisonLabel={t("prevPeriod")}
              />
              <AppointmentsHeatmap data={heatmapData} isLoading={loadingHeatmap} />
            </div>

            {/* Funil + Tipos de atendimento */}
            <div className="grid gap-5 xl:grid-cols-2">
              <AppointmentsFunnel
                kpis={overview?.kpis ?? null}
                isLoading={loadingOverview}
                formatCurrency={formatCurrency}
              />
              <AppointmentTypesPanel
                rows={aggregatedAppointmentTypes}
                isLoading={loadingByProfessional}
                formatCurrency={formatCurrency}
              />
            </div>

          </TabsContent>
        )}

        {/* ================================================================
            FINANCIAL — Revenue Redesign
        ================================================================ */}
        {canViewFinancialReports && (
          <TabsContent value="financial" className="space-y-6">
            {/* Period Hero */}
            <RevenuePeriodHero
              dateFrom={finDateFrom}
              dateTo={finDateTo}
              compareMode={finCompareMode}
              netRevenue={revenueReport?.current.netRevenue ?? 0}
              netRevenuePrev={revenueReport?.previous?.netRevenue ?? 0}
              onDateFromChange={setFinDateFrom}
              onDateToChange={setFinDateTo}
              onCompareModeChange={setFinCompareMode}
            />

            {/* KPIs */}
            {loadingRevenue ? (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {[0,1,2,3].map((i) => <Skeleton key={i} className="h-36 w-full" />)}
              </div>
            ) : revenueReport ? (
              <RevenueKPIs current={revenueReport.current} previous={revenueReport.previous ?? revenueReport.current} />
            ) : null}

            {/* Revenue Line Chart */}
            {loadingRevenue ? (
              <Skeleton className="h-64 w-full" />
            ) : revenueReport ? (
              <RevenueLineChart data={revenueReport.dailyRevenue} />
            ) : null}

            {/* Payment Methods + Category Margin */}
            <div className="grid gap-5 xl:grid-cols-2">
              {loadingRevenue ? (
                <>
                  <Skeleton className="h-64 w-full" />
                  <Skeleton className="h-64 w-full" />
                </>
              ) : revenueReport ? (
                <>
                  <RevenuePaymentMethods data={revenueReport.paymentMethods} />
                  <RevenueCategoryMargin data={revenueReport.categoryRevenue} />
                </>
              ) : null}
            </div>

            {/* Receivables */}
            {loadingRevenue ? (
              <Skeleton className="h-48 w-full" />
            ) : revenueReport ? (
              <RevenueReceivables
                data={revenueReport.receivables}
                total={revenueReport.receivables.reduce((s, r) => s + r.total, 0)}
              />
            ) : null}

            {/* ABC Curve */}
            {loadingRevenue ? (
              <Skeleton className="h-64 w-full" />
            ) : revenueReport ? (
              <RevenueAbcCurve data={revenueReport.abcPets} />
            ) : null}

          </TabsContent>

        )}

        {/* ================================================================
            PROFESSIONALS — Profissionais redesign
        ================================================================ */}
        {canViewFinancialReports && (
          <TabsContent value="professionals" className="space-y-6">
            {/* Period Hero */}
            <ProfessionalsPeriodHero
              dateFrom={profDateFrom}
              dateTo={profDateTo}
              activeProfessionals={professionalTabSummary.activeProfessionals}
              totalAppointments={professionalTabSummary.totalAppointments}
              totalRevenue={professionalTabSummary.totalRevenue}
              totalCommission={professionalTabSummary.totalCommission}
              onDateFromChange={setProfDateFrom}
              onDateToChange={setProfDateTo}
            />

            {/* Main Table */}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle>{t("attendancesByProfessional")}</CardTitle>
                    <CardDescription>{t("professionalTableDesc")}</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportProfessionalsTab}
                    disabled={professionalTabRows.length === 0}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {t("export")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingByProfessionalTab ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-14 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                          <TableHead>{t("professional")}</TableHead>
                          <TableHead className="text-right">{t("attendances")}</TableHead>
                          <TableHead className="text-right">{t("revenue")}</TableHead>
                          <TableHead className="text-right">{t("commission")}</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {professionalTabRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="h-32 text-center text-sm text-muted-foreground">
                              {t("emptyBreakdown")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          [...professionalTabRows]
                            .sort((a, b) => b.receita - a.receita)
                            .map((row) => {
                              const rowId = row.vetUserId ?? "null";
                              const isExpanded = selectedProfessionalId === rowId;
                              return (
                                <Fragment key={rowId}>
                                  <TableRow className={isExpanded ? "bg-primary/5 hover:bg-primary/5" : undefined}>
                                    <TableCell>
                                      <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                                          {row.professionalName.slice(0, 1).toUpperCase()}
                                        </div>
                                        <div>
                                          <p className="font-medium">{row.professionalName}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {t("professionalRevenueShare", {
                                              value:
                                                professionalTabSummary.totalRevenue > 0
                                                  ? ((row.receita / professionalTabSummary.totalRevenue) * 100).toFixed(1)
                                                  : "0.0",
                                            })}
                                          </p>
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right">{row.totalAppointments}</TableCell>
                                    <TableCell className="text-right font-semibold">{formatCurrency(row.receita)}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(row.commission)}</TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        aria-expanded={isExpanded}
                                        onClick={() => setSelectedProfessionalId(isExpanded ? null : rowId)}
                                      >
                                        {isExpanded ? t("profDeepDiveHide") : t("profDeepDiveView")}
                                        {isExpanded ? (
                                          <ChevronLeft className="h-4 w-4 ml-1" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4 ml-1" />
                                        )}
                                      </Button>
                                    </TableCell>
                                  </TableRow>

                                  {isExpanded && (
                                    <TableRow key={`${rowId}-details`} className="bg-muted/20 hover:bg-muted/20">
                                      <TableCell colSpan={5} className="p-0">
                                        <div className="space-y-5 border-t p-4 lg:p-5">
                                          {/* Header */}
                                          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                                            <div>
                                              <h3 className="text-base font-semibold">
                                                {t("professionalDetailTitle", { name: row.professionalName })}
                                              </h3>
                                              <p className="text-sm text-muted-foreground">{t("professionalDetailDesc")}</p>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                  const allItems = [...row.services, ...row.products].filter((item) => item.commission > 0);
                                                  if (allItems.length === 0) return;
                                                  exportToExcel({
                                                    fileName: `${t("commissionExportFileName")}_${row.professionalName}_${format(new Date(), "yyyyMMdd")}.xlsx`,
                                                    sheetName: t("commissionExportSheet"),
                                                    rows: allItems.map((item) => ({
                                                      [t("profExportItemName")]: item.itemName,
                                                      [t("profExportQty")]: item.quantity,
                                                      [t("profExportAppointmentCount")]: item.appointmentCount,
                                                      [t("revenue")]: +item.revenue.toFixed(2),
                                                      [t("commission")]: +item.commission.toFixed(2),
                                                    })),
                                                  });
                                                }}
                                              >
                                                <Download className="h-4 w-4 mr-1" />
                                                {t("commissionExportButton")}
                                              </Button>
                                              <Badge variant="outline" className="w-fit">
                                                {t("professionalDetailBadge", {
                                                  value:
                                                    professionalTabSummary.totalRevenue > 0
                                                      ? ((row.receita / professionalTabSummary.totalRevenue) * 100).toFixed(1)
                                                      : "0.0",
                                                })}
                                              </Badge>
                                            </div>
                                          </div>

                                          {/* KPI Strip */}
                                          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                                            {[
                                              { label: t("attendances"), value: String(row.totalAppointments) },
                                              { label: t("revenue"), value: formatCurrency(row.receita) },
                                              { label: t("profDeepDiveCommission"), value: formatCurrency(row.commission) },
                                              {
                                                label: t("profDeepDiveTicket"),
                                                value:
                                                  row.totalAppointments > 0
                                                    ? formatCurrency(row.receita / row.totalAppointments)
                                                    : "—",
                                              },
                                              { label: t("finishedPct"), value: `${row.pctFinalizados.toFixed(1)}%` },
                                            ].map((metric) => (
                                              <div key={metric.label} className="rounded-lg border bg-background p-3">
                                                <p className="text-xs text-muted-foreground">{metric.label}</p>
                                                <p className="mt-1 text-lg font-semibold">{metric.value}</p>
                                              </div>
                                            ))}
                                          </div>

                                          {/* Commission by type + Appointment types */}
                                          <div className="grid gap-4 xl:grid-cols-2">
                                            <div className="rounded-lg border bg-background p-4">
                                              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                                <Receipt className="h-4 w-4 text-primary" />
                                                {t("commissionByTypeTitle")}
                                              </h3>
                                              {(() => {
                                                const commissionItems = [...row.services, ...row.products]
                                                  .filter((item) => item.commission > 0)
                                                  .sort((a, b) => b.commission - a.commission);
                                                const COMM_PAGE_SIZE = 5;
                                                const commPage = commissionPages[rowId] ?? 1;
                                                const totalCommPages = Math.max(1, Math.ceil(commissionItems.length / COMM_PAGE_SIZE));
                                                const pagedItems = commissionItems.slice((commPage - 1) * COMM_PAGE_SIZE, commPage * COMM_PAGE_SIZE);
                                                const totalCommission = commissionItems.reduce((s, i) => s + i.commission, 0);
                                                if (commissionItems.length === 0) {
                                                  return <p className="text-sm text-muted-foreground">{t("commissionByTypeEmpty")}</p>;
                                                }
                                                return (
                                                  <div className="space-y-3">
                                                    {pagedItems.map((item) => (
                                                      <div key={item.itemId ?? item.itemName}>
                                                        <div className="flex items-center justify-between gap-2 text-sm">
                                                          <span className="font-medium truncate">{item.itemName}</span>
                                                          <span className="font-semibold shrink-0">{formatCurrency(item.commission)}</span>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground mb-1">
                                                          {t("commissionOfTotal", {
                                                            value:
                                                              totalCommission > 0
                                                                ? ((item.commission / totalCommission) * 100).toFixed(1)
                                                                : "0.0",
                                                          })}
                                                        </p>
                                                        <Progress
                                                          value={totalCommission > 0 ? (item.commission / totalCommission) * 100 : 0}
                                                          className="h-1.5"
                                                        />
                                                      </div>
                                                    ))}
                                                    {totalCommPages > 1 && (
                                                      <div className="flex items-center justify-between pt-1">
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          disabled={commPage <= 1}
                                                          onClick={() => setCommissionPages((p) => ({ ...p, [rowId]: commPage - 1 }))}
                                                        >
                                                          <ChevronLeft className="h-4 w-4" />
                                                        </Button>
                                                        <span className="text-xs text-muted-foreground">{commPage} / {totalCommPages}</span>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          disabled={commPage >= totalCommPages}
                                                          onClick={() => setCommissionPages((p) => ({ ...p, [rowId]: commPage + 1 }))}
                                                        >
                                                          <ChevronRight className="h-4 w-4" />
                                                        </Button>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })()}
                                            </div>

                                            <div className="rounded-lg border bg-background p-4">
                                              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                                <Activity className="h-4 w-4 text-primary" />
                                                {t("appointmentTypesBreakdown")}
                                              </h3>
                                              {row.appointmentTypes.length === 0 ? (
                                                <p className="text-sm text-muted-foreground">{t("emptyBreakdown")}</p>
                                              ) : (
                                                <div className="space-y-2">
                                                  {row.appointmentTypes.map((tp) => (
                                                    <div
                                                      key={tp.type}
                                                      className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
                                                    >
                                                      <span className="font-medium truncate">{tp.typeLabel}</span>
                                                      <div className="flex items-center gap-3 shrink-0 text-right">
                                                        <span className="text-muted-foreground">{tp.totalAppointments}</span>
                                                        <span className="font-semibold">{formatCurrency(tp.receita)}</span>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Services + Products */}
                                          <div className="grid gap-4 xl:grid-cols-2">
                                            <div className="rounded-lg border bg-background p-4">
                                              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                                <Stethoscope className="h-4 w-4 text-primary" />
                                                {t("servicesBreakdown")}
                                              </h3>
                                              {row.services.length === 0 ? (
                                                <p className="text-sm text-muted-foreground">{t("emptyBreakdown")}</p>
                                              ) : (
                                                <div className="space-y-1.5">
                                                  {row.services.map((item) => (
                                                    <div
                                                      key={item.itemId ?? item.itemName}
                                                      className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
                                                    >
                                                      <div className="min-w-0">
                                                        <p className="font-medium truncate">{item.itemName}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                          {item.quantity} {t("qty").toLowerCase()} · {item.appointmentCount}{" "}
                                                          {t("attendances").toLowerCase()}
                                                        </p>
                                                      </div>
                                                      <p className="font-semibold shrink-0">
                                                        {item.revenue > 0 ? formatCurrency(item.revenue) : "—"}
                                                      </p>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>

                                            <div className="rounded-lg border bg-background p-4">
                                              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                                <Receipt className="h-4 w-4 text-primary" />
                                                {t("productsBreakdown")}
                                              </h3>
                                              {row.products.length === 0 ? (
                                                <p className="text-sm text-muted-foreground">{t("emptyBreakdown")}</p>
                                              ) : (
                                                <div className="space-y-1.5">
                                                  {row.products.map((item) => (
                                                    <div
                                                      key={item.itemId ?? item.itemName}
                                                      className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
                                                    >
                                                      <div className="min-w-0">
                                                        <p className="font-medium truncate">{item.itemName}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                          {item.quantity} {t("qty").toLowerCase()} · {item.appointmentCount}{" "}
                                                          {t("attendances").toLowerCase()}
                                                        </p>
                                                      </div>
                                                      <p className="font-semibold shrink-0">
                                                        {item.revenue > 0 ? formatCurrency(item.revenue) : "—"}
                                                      </p>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Patients */}
                                          {(() => {
                                            const PATIENTS_PER_PAGE = 10;
                                            const currentPage = patientPages[rowId] ?? 1;
                                            const totalPatients = row.patients.length;
                                            const totalPages = Math.ceil(totalPatients / PATIENTS_PER_PAGE) || 1;
                                            const pagePatients = row.patients.slice(
                                              (currentPage - 1) * PATIENTS_PER_PAGE,
                                              currentPage * PATIENTS_PER_PAGE,
                                            );
                                            return (
                                              <div className="rounded-lg border bg-background p-4">
                                                <div className="mb-3 flex items-center justify-between gap-2">
                                                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                                                    <Users className="h-4 w-4 text-primary" />
                                                    {t("patientsByProfessionalBreakdown")}
                                                  </h3>
                                                  {totalPatients > 0 && (
                                                    <span className="text-xs text-muted-foreground">{totalPatients}</span>
                                                  )}
                                                </div>
                                                {totalPatients === 0 ? (
                                                  <p className="text-sm text-muted-foreground">{t("emptyBreakdown")}</p>
                                                ) : (
                                                  <>
                                                    <div className="space-y-1.5">
                                                      {pagePatients.map((patient) => (
                                                        <div
                                                          key={patient.petId}
                                                          className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
                                                        >
                                                          <div className="min-w-0">
                                                            <p className="font-medium truncate">{patient.petName}</p>
                                                            <p className="text-xs text-muted-foreground truncate">
                                                              {patient.ownerName ?? t("owner")}
                                                              {[patient.speciesName, patient.breedName].filter(Boolean).length > 0 && (
                                                                <> · {[patient.speciesName, patient.breedName].filter(Boolean).join(" · ")}</>
                                                              )}
                                                            </p>
                                                          </div>
                                                          <div className="flex items-center gap-2 shrink-0">
                                                            {patient.revenue > 0 && (
                                                              <span className="text-xs text-muted-foreground">{formatCurrency(patient.revenue)}</span>
                                                            )}
                                                            <Badge variant="secondary">{patient.appointmentCount}</Badge>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                    {totalPages > 1 && (
                                                      <div className="flex items-center justify-between pt-3 mt-3 border-t">
                                                        <p className="text-xs text-muted-foreground">
                                                          {(currentPage - 1) * PATIENTS_PER_PAGE + 1}–{Math.min(currentPage * PATIENTS_PER_PAGE, totalPatients)} {t("of")} {totalPatients}
                                                        </p>
                                                        <div className="flex items-center gap-1">
                                                          <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 px-2"
                                                            disabled={currentPage === 1}
                                                            onClick={() => setPatientPages((prev) => ({ ...prev, [rowId]: currentPage - 1 }))}
                                                          >
                                                            <ChevronLeft className="h-3.5 w-3.5" />
                                                          </Button>
                                                          <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 px-2"
                                                            disabled={currentPage >= totalPages}
                                                            onClick={() => setPatientPages((prev) => ({ ...prev, [rowId]: currentPage + 1 }))}
                                                          >
                                                            <ChevronRight className="h-3.5 w-3.5" />
                                                          </Button>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </Fragment>
                              );
                            })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
        {/* ================================================================
            STOCK (Phase 1.5.4)
        ================================================================ */}
        {canViewStockReports && (
          <TabsContent value="stock" className="space-y-5">
            {/* Period Hero */}
            <StockPeriodHero
              dateFrom={stockDateFrom}
              dateTo={stockDateTo}
              quantityIn={stock?.quantityIn ?? 0}
              quantityOut={stock?.quantityOut ?? 0}
              adjustments={stock?.adjustments ?? 0}
              lowStockCount={stock?.lowStockCount ?? 0}
              criticalStockCount={stock?.criticalStockCount ?? 0}
              onDateFromChange={setStockDateFrom}
              onDateToChange={setStockDateTo}
            />

            {/* Rankings */}
            <div className="grid gap-4 xl:grid-cols-3">
              <ItemRankingCard
                title={t("productRankingTitle")}
                description={t("productRankingDesc")}
                ranking={itemRankingsByType.products}
                badgeLabel={t("product")}
              />
              <ItemRankingCard
                title={t("serviceRankingTitle")}
                description={t("serviceRankingDesc")}
                ranking={itemRankingsByType.services}
                badgeLabel={t("service")}
              />
              <ItemRankingCard
                title={t("packageRankingTitle")}
                description={t("packageRankingDesc")}
                ranking={itemRankingsByType.packages}
                badgeLabel={t("package")}
              />
            </div>

            {/* Movement table */}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle>{t("stockTitle")}</CardTitle>
                    <CardDescription>{t("stockDesc")}</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExportStock} disabled={(stock?.movementRows.length ?? 0) === 0}>
                    <Download className="h-4 w-4 mr-1" />
                    {t("export")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingStock ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium">{t("search")}</label>
                        <Input placeholder={t("searchByName")} value={filterOutflowSearch} onChange={(e) => { setFilterOutflowSearch(e.target.value); setPageOutflow(1); }} className="min-w-[200px]" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium">{t("sortBy")}</label>
                        <Select value={sortOutflow} onValueChange={(v) => { setSortOutflow(v); setPageOutflow(1); }}>
                          <SelectTrigger className="w-full min-w-[220px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SORT_OUTFLOW_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="overflow-x-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/40 hover:bg-muted/40">
                            <TableHead>{t("item")}</TableHead>
                            <TableHead className="text-right">{t("stockEntries")}</TableHead>
                            <TableHead className="text-right">{t("stockExits")}</TableHead>
                            <TableHead className="text-right">{t("revenue")}</TableHead>
                            <TableHead className="text-right">{t("stockCurrentQty")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginationOutflow.items.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="h-32 text-center text-sm text-muted-foreground">
                                {t("stockEmpty")}
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginationOutflow.items.map((row) => {
                              const revenue = stockRevenueByItemId[row.itemId] ?? 0;
                              const currentQty = stock?.currentStockByItemId[row.itemId] ?? "—";
                              return (
                              <TableRow key={row.itemId}>
                                <TableCell className="font-medium">{row.itemName}</TableCell>
                                <TableCell className="text-right">{row.quantityIn}</TableCell>
                                <TableCell className="text-right">{row.quantityOut}</TableCell>
                                <TableCell className="text-right">{revenue > 0 ? formatCurrency(revenue) : "—"}</TableCell>
                                <TableCell className="text-right">{currentQty}</TableCell>
                              </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    <PaginationBar page={pageOutflow} pages={paginationOutflow.pages} total={paginationOutflow.total} setPage={setPageOutflow} />
                    {(stock?.lowStockRows.length ?? 0) > 0 && (
                      <div className="mt-6 border-t pt-4">
                        <h3 className="text-sm font-semibold mb-3">{t("lowStockItems")}</h3>
                        <div className="flex flex-wrap gap-2">
                          {stock?.lowStockRows.slice(0, 10).map((row) => (
                            <Badge key={row.stockId} variant={row.quantity <= 0 ? "destructive" : "secondary"}>
                              {row.itemName}: {row.quantity} {row.unit ?? ""}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ================================================================
            EXPLORADOR DE VENDAS E ATENDIMENTOS
        ================================================================ */}
        {canViewFinancialReports && (
          <TabsContent value="salesExplorer" className="space-y-6">
            <SalesExplorerTab />
          </TabsContent>
        )}

        {/* ================================================================
            EXPLORER DE BASE (Phase 1.5.3)
        ================================================================ */}
        {canViewReports && (
          <TabsContent value="explorer" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle>{t("executiveOpportunitiesTitle")}</CardTitle>
                    <CardDescription>{t("executiveOpportunitiesDesc")}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportExecutiveOpportunities}>
                      <Download className="h-4 w-4 mr-1" />
                      {t("export")}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportInactive} disabled={inactiveOwners.length === 0}>
                      <Download className="h-4 w-4 mr-1" />
                      {t("exportDormant")}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("dormantDays")}</label>
                      <Select value={String(inactiveDays)} onValueChange={(v) => { setInactiveDays(Number(v)); setPageInactive(1); }}>
                        <SelectTrigger className="w-full min-w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {INACTIVE_DAYS_OPTIONS.map((d) => (
                            <SelectItem key={d} value={String(d)}>{t("inactiveDaysOption", { count: d })}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("search")}</label>
                      <Input placeholder={t("searchByOwner")} value={inactiveSearch} onChange={(e) => { setInactiveSearch(e.target.value); setPageInactive(1); }} className="min-w-[200px]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:min-w-[480px]">
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("dormantClients")}</p>
                      <p className="text-2xl font-bold tabular-nums">{loadingInactive ? "..." : paginationInactive.total}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("pets")}</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {loadingInactive ? "..." : paginationInactive.fullList.reduce((sum, row) => sum + row.petCount, 0)}
                      </p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("totalHistoricalSpent")}</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {loadingInactive
                          ? "..."
                          : formatCurrency(paginationInactive.fullList.reduce((sum, row) => sum + row.totalHistoricalSpent, 0))}
                      </p>
                    </div>
                  </div>
                </div>
                {!loadingInactive && paginationInactive.items.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {paginationInactive.items.slice(0, 4).map((row) => (
                      <Badge key={row.ownerId} variant="secondary" className="max-w-full">
                        {row.ownerName} · {t("daysAgo", { count: row.daysSinceLastVisit })}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="mt-6 border-t pt-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold">{t("overdueVaccinesTitle")}</h3>
                      <p className="text-sm text-muted-foreground">{t("overdueVaccinesDesc")}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={handleExportOverdueVaccines} disabled={overdueVaccines.length === 0}>
                        <Download className="h-4 w-4 mr-1" />
                        {t("export")}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("application")}</p>
                      <p className="text-2xl font-bold tabular-nums">{loadingOverdueVaccines ? "..." : overdueVaccines.length}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("owner")}</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {loadingOverdueVaccines ? "..." : new Set(overdueVaccines.map((row) => row.ownerId)).size}
                      </p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("daysOverdue")}</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {loadingOverdueVaccines
                          ? "..."
                          : overdueVaccines.length === 0
                            ? 0
                            : Math.max(...overdueVaccines.map((row) => row.daysOverdue))}
                      </p>
                    </div>
                  </div>
                  {!loadingOverdueVaccines && overdueVaccines.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {overdueVaccines.slice(0, 4).map((row) => (
                        <Badge key={row.planId} variant="secondary" className="max-w-full">
                          {row.petName} · {row.itemName} · {t("daysAgo", { count: row.daysOverdue })}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6 border-t pt-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold">{t("upcomingApplicationsTitle")}</h3>
                      <p className="text-sm text-muted-foreground">{t("upcomingApplicationsDesc")}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={handleExportUpcomingApplications} disabled={upcomingApplications.length === 0}>
                        <Download className="h-4 w-4 mr-1" />
                        {t("export")}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("application")}</p>
                      <p className="text-2xl font-bold tabular-nums">{loadingUpcomingApplications ? "..." : upcomingApplications.length}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("owner")}</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {loadingUpcomingApplications ? "..." : new Set(upcomingApplications.map((row) => row.ownerId)).size}
                      </p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("next30Days")}</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {loadingUpcomingApplications
                          ? "..."
                          : upcomingApplications.length === 0
                            ? 0
                            : Math.min(...upcomingApplications.map((row) => row.daysUntilDue))}
                      </p>
                    </div>
                  </div>
                  {!loadingUpcomingApplications && upcomingApplications.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {upcomingApplications.slice(0, 4).map((row) => (
                        <Badge key={row.planId} variant="secondary" className="max-w-full">
                          {row.petName} · {row.itemName} · {t("daysUntilDueValue", { count: row.daysUntilDue })}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6 border-t pt-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold">{t("campaignsInPeriod")}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t("campaignsPeriodSummary", {
                          sent: campaignPeriodSummary.sent,
                          read: campaignPeriodSummary.read,
                        })}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigate("/reports/campaigns")}>
                      {t("tabCampaigns")}
                    </Button>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("campaignsInPeriod")}</p>
                      <p className="text-2xl font-bold tabular-nums">{campaignPeriodSummary.count}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("revenue")}</p>
                      <p className="text-2xl font-bold tabular-nums">{formatCurrency(campaignPeriodSummary.revenue)}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">{t("roi")}</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {campaignPeriodSummary.roi === null
                          ? t("campaignsRoiUnavailable")
                          : `${(campaignPeriodSummary.roi * 100).toFixed(0)}%`}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle>{t("explorerTitle")}</CardTitle>
                    <CardDescription>{t("explorerDesc")}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportExplorer} disabled={paginationExplorer.total === 0}>
                      <Download className="h-4 w-4 mr-1" />
                      {t("export")}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("clientsA")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartClientsA")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.clientsA}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("vipDormant")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartVipDormant")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.vipDormant}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("potential")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartPotentialClients")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.potentialClients}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("puppies")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartPuppies")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.puppies}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("seniors")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartSeniors")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.seniors}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("geriatric")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartGeriatric")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.geriatric}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("highTicket")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartHighTicket")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.highTicket}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("lowRecurrence")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartLowRecurrence")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.lowRecurrence}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyExplorerSmartView("staleWeight")}
                    className="rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted"
                  >
                    <p className="text-xs text-muted-foreground">{t("smartStaleWeight")}</p>
                    <p className="text-2xl font-bold tabular-nums">{explorerSmartViewSummary.staleWeight}</p>
                  </button>
                </div>
                {/* Filter panel */}
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("search")}</label>
                    <Input
                      placeholder={t("explorerSearchPlaceholder")}
                      value={explorerSearch}
                      onChange={(e) => { setExplorerSearch(e.target.value); setPageExplorer(1); }}
                      className="min-w-[220px]"
                    />
                  </div>
                  {explorerSpeciesOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("species")}</label>
                      <Select value={explorerSpecies} onValueChange={(v) => { setExplorerSpecies(v); setExplorerBreed("all"); setPageExplorer(1); }}>
                        <SelectTrigger className="w-full min-w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("allSpecies")}</SelectItem>
                          {explorerSpeciesOptions.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {explorerBreedOptions.length > 0 && explorerSpecies !== "all" && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("breed")}</label>
                      <Select value={explorerBreed} onValueChange={(v) => { setExplorerBreed(v); setPageExplorer(1); }}>
                        <SelectTrigger className="w-full min-w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("allBreeds")}</SelectItem>
                          {explorerBreedOptions.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {explorerGenderOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("explorerGender")}</label>
                      <Select value={explorerGender} onValueChange={(v) => { setExplorerGender(v); setPageExplorer(1); }}>
                        <SelectTrigger className="w-full min-w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("allGenders")}</SelectItem>
                          {explorerGenderOptions.map((gender) => (
                            <SelectItem key={gender.id} value={gender.id}>{gender.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {explorerSizeOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("explorerSize")}</label>
                      <Select value={explorerSize} onValueChange={(v) => { setExplorerSize(v); setPageExplorer(1); }}>
                        <SelectTrigger className="w-full min-w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("allSizes")}</SelectItem>
                          {explorerSizeOptions.map((size) => (
                            <SelectItem key={size.id} value={size.id}>{size.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {explorerCoatTypeOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("explorerCoatType")}</label>
                      <Select value={explorerCoatType} onValueChange={(v) => { setExplorerCoatType(v); setPageExplorer(1); }}>
                        <SelectTrigger className="w-full min-w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("allCoatTypes")}</SelectItem>
                          {explorerCoatTypeOptions.map((coatType) => (
                            <SelectItem key={coatType.id} value={coatType.id}>{coatType.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("lastVisit")}</label>
                    <Select value={explorerLastVisitDays} onValueChange={(v) => { setExplorerLastVisitDays(v); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[160px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="30">{t("lastVisitMoreThanDays", { count: 30 })}</SelectItem>
                        <SelectItem value="60">{t("lastVisitMoreThanDays", { count: 60 })}</SelectItem>
                        <SelectItem value="90">{t("lastVisitMoreThanDays", { count: 90 })}</SelectItem>
                        <SelectItem value="180">{t("lastVisitMoreThanDays", { count: 180 })}</SelectItem>
                        <SelectItem value="never">{t("lastVisitNever")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("explorerPetStatus")}</label>
                    <Select value={explorerPetStatus} onValueChange={(v) => { setExplorerPetStatus(v as "all" | "alive" | "deceased"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="alive">{t("explorerAlive")}</SelectItem>
                        <SelectItem value="deceased">{t("explorerDeceased")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("explorerAge")}</label>
                    <Select value={explorerAgeGroup} onValueChange={(v) => { setExplorerAgeGroup(v as AgeGroup | "all"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="under1">{t("explorerAgeUnder1")}</SelectItem>
                        <SelectItem value="1to7">{t("explorerAge1to7")}</SelectItem>
                        <SelectItem value="7plus">{t("explorerAge7plus")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("explorerDetailedAge")}</label>
                    <Select value={explorerDetailedAgeGroup} onValueChange={(v) => { setExplorerDetailedAgeGroup(v as DetailedAgeGroup | "all"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="puppy">{t("detailedAge.puppy")}</SelectItem>
                        <SelectItem value="adult">{t("detailedAge.adult")}</SelectItem>
                        <SelectItem value="senior">{t("detailedAge.senior")}</SelectItem>
                        <SelectItem value="geriatric">{t("detailedAge.geriatric")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("explorerAgeRange")}</label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        placeholder={t("explorerAgeMin")}
                        value={explorerAgeMin}
                        onChange={(e) => { setExplorerAgeMin(e.target.value); setPageExplorer(1); }}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">{t("to")}</span>
                      <Input
                        type="number"
                        min={0}
                        placeholder={t("explorerAgeMax")}
                        value={explorerAgeMax}
                        onChange={(e) => { setExplorerAgeMax(e.target.value); setPageExplorer(1); }}
                        className="w-24"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("relationshipStatus")}</label>
                    <Select value={explorerRelationshipStatus} onValueChange={(v) => { setExplorerRelationshipStatus(v as RelationshipStatus | "all"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[160px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="new">{t("relationship.new")}</SelectItem>
                        <SelectItem value="active">{t("relationship.active")}</SelectItem>
                        <SelectItem value="recurring">{t("relationship.recurring")}</SelectItem>
                        <SelectItem value="atRisk">{t("relationship.atRisk")}</SelectItem>
                        <SelectItem value="dormant">{t("relationship.dormant")}</SelectItem>
                        <SelectItem value="lost">{t("relationship.lost")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("sterilized")}</label>
                    <Select value={explorerSterilized} onValueChange={(v) => { setExplorerSterilized(v as "all" | "yes" | "no"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[120px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="yes">{t("yes")}</SelectItem>
                        <SelectItem value="no">{t("no")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("weightStatus")}</label>
                    <Select value={explorerWeightStatus} onValueChange={(v) => { setExplorerWeightStatus(v as "all" | "with" | "missing" | "stale"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[160px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="with">{t("weightWith")}</SelectItem>
                        <SelectItem value="missing">{t("weightMissing")}</SelectItem>
                        <SelectItem value="stale">{t("weightStale")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("minVisits")}</label>
                    <Input
                      type="number"
                      min={0}
                      value={explorerMinVisits}
                      onChange={(e) => { setExplorerMinVisits(e.target.value); setPageExplorer(1); }}
                      className="w-28"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("minAverageTicket")}</label>
                    <Input
                      type="number"
                      min={0}
                      value={explorerMinTicket}
                      onChange={(e) => { setExplorerMinTicket(e.target.value); setPageExplorer(1); }}
                      className="w-32"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("abcCurve")}</label>
                    <Select value={explorerAbc} onValueChange={(v) => { setExplorerAbc(v as ExplorerAbcCurve | "all"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[120px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="A">{t("curveA")}</SelectItem>
                        <SelectItem value="B">{t("curveB")}</SelectItem>
                        <SelectItem value="C">{t("curveC")}</SelectItem>
                        <SelectItem value="none">{t("explorerNoRevenue")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("explorerWhatsApp")}</label>
                    <Select value={explorerHasWhatsApp} onValueChange={(v) => { setExplorerHasWhatsApp(v as "all" | "yes" | "no"); setPageExplorer(1); }}>
                      <SelectTrigger className="w-full min-w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all")}</SelectItem>
                        <SelectItem value="yes">{t("explorerWithWhatsApp")}</SelectItem>
                        <SelectItem value="no">{t("explorerWithoutWhatsApp")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {loadingExplorer ? (
                  <Skeleton className="h-40 w-full" />
                ) : (
                  <>
                    {paginationExplorer.total > 0 && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {t("explorerCount", { count: paginationExplorer.total })}
                      </p>
                    )}
                    <div className="space-y-3">
                      {paginationExplorer.items.length === 0 ? (
                        <p className="text-center py-10 text-muted-foreground">{t("explorerEmpty")}</p>
                      ) : (
                        paginationExplorer.items.map((row) => (
                          <div key={row.ownerId} className="border rounded-lg p-4 space-y-2">
                            {/* Owner header */}
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{row.ownerName}</span>
                                {row.abcCurve !== "none" && <AbcBadge curve={row.abcCurve as import("@/hooks/useReportsFinancial").AbcCurve} />}
                                {row.hasWhatsApp && (
                                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 border-green-200">
                                    WhatsApp
                                  </Badge>
                                )}
                                {!row.hasPhone && (
                                  <Badge variant="secondary" className="text-xs text-muted-foreground">
                                    {t("explorerNoPhone")}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <Badge variant="outline" className="text-xs">
                                  {t(`relationship.${row.relationshipStatus}`)}
                                </Badge>
                                <span>{t("visitCount", { count: row.visitCount })}</span>
                                {row.totalHistoricalSpent > 0 && (
                                  <span>{formatCurrency(row.totalHistoricalSpent)}</span>
                                )}
                                {row.averageTicket > 0 && (
                                  <span>{t("averageTicket")}: {formatCurrency(row.averageTicket)}</span>
                                )}
                                {row.lastVisitDate && (
                                  <span>{t("lastVisit")}: {formatDate(row.lastVisitDate)}</span>
                                )}
                              </div>
                            </div>
                            {(row.neighborhood || row.cityName || row.createdAt || row.marketingConsent !== null) && (
                              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                                {row.createdAt && <span>{t("createdAt")}: {formatDate(row.createdAt)}</span>}
                                {row.neighborhood && <span>{t("neighborhood")}: {row.neighborhood}</span>}
                                {row.cityName && <span>{t("city")}: {row.cityName}{row.stateCode ? `/${row.stateCode}` : ""}</span>}
                                {row.marketingConsent !== null && (
                                  <span>{t("marketingConsent")}: {row.marketingConsent ? t("yes") : t("no")}</span>
                                )}
                              </div>
                            )}
                            {/* Pets row */}
                            {row.pets.length > 0 && (
                              <div className="flex flex-wrap gap-2 pl-1">
                                {row.pets.map((p) => (
                                  <div key={p.petId} className="flex items-center gap-1 text-sm bg-muted/50 rounded px-2 py-0.5">
                                    <span className="font-medium">{p.petName}</span>
                                    {p.speciesName && (
                                      <span className="text-muted-foreground">· {p.speciesName}</span>
                                    )}
                                    {p.breedName && (
                                      <span className="text-muted-foreground">· {p.breedName}</span>
                                    )}
                                    {p.sizeCategoryName && (
                                      <span className="text-muted-foreground">· {p.sizeCategoryName}</span>
                                    )}
                                    {p.coatTypeName && (
                                      <span className="text-muted-foreground">· {p.coatTypeName}</span>
                                    )}
                                    {p.currentWeight !== null && (
                                      <span className="text-muted-foreground">· {p.currentWeight}kg</span>
                                    )}
                                    {p.sterilizationName && (
                                      <span className="text-muted-foreground">· {p.sterilizationName}</span>
                                    )}
                                    {p.lastConsultationDate && (
                                      <span className="text-muted-foreground">· {t("lastConsultation")}: {formatDate(p.lastConsultationDate)}</span>
                                    )}
                                    {p.nextApplicationDate && (
                                      <span className="text-muted-foreground">· {t("nextApplication")}: {formatDate(p.nextApplicationDate)}</span>
                                    )}
                                    {p.conditionNames.length > 0 && (
                                      <span className="text-muted-foreground">· {p.conditionNames.join(", ")}</span>
                                    )}
                                    {!p.isAlive && (
                                      <Badge variant="secondary" className="text-xs ml-1">{t("explorerDeceased")}</Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    <PaginationBar page={pageExplorer} pages={paginationExplorer.pages} total={paginationExplorer.total} setPage={setPageExplorer} />
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ================================================================
            APPLICATIONS
        ================================================================ */}
        {canViewReports && (
          <TabsContent value="campaigns" className="space-y-6">
            <CampaignsPage />
          </TabsContent>
        )}

        {canViewApplications && (
          <TabsContent value="applications">
            {isApplicationsTabActive && <ApplicationReport variant="embedded" />}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
